import React, { useState, useEffect, useRef, useCallback } from "react";
import { useBoard, useOracle, useYesterday, useLiveGames, useBatters, usePlays,
         useTodaysPitchers, useAiPredictions, useBookStates, useScheduleSummary,
         useGamePitchers, useLineMoves } from "./lib/hooks";
import { saveAiPrediction, upsertPollVote, fetchPollVotes } from "./lib/supabase";

// ── DESIGN TOKENS ─────────────────────────────────────────────
const D = {
  bg:"#000000", s1:"#1C1C1E", s2:"#2C2C2E", s3:"#3A3A3C",
  border:"rgba(84,84,88,.65)", borderL:"rgba(84,84,88,.35)",
  t1:"#FFFFFF", t2:"rgba(235,235,245,.6)", t3:"rgba(235,235,245,.3)", t4:"rgba(235,235,245,.18)",
  blue:"#0A84FF", green:"#30D158", red:"#FF453A", amber:"#FF9F0A",
  gold:"#FFD60A", violet:"#BF5AF2", cyan:"#5AC8FA", teal:"#5AC8FA",
  live:"#30D158", oracle:"#5AC8FA",
};
const SANS = "-apple-system,BlinkMacSystemFont,'SF Pro Text','SF Pro Display','Helvetica Neue',sans-serif";
const MONO = "'SF Mono','Fira Mono','Courier New',monospace";

const BOOKS = {
  hardrockbet:{ label:"Hard Rock Bet", short:"HRB", c:"#FF375F", link:"https://hardrock.bet", linkAndroid:"https://hardrock.bet" },
  fanduel:    { label:"FanDuel",        short:"FD",  c:"#1493FF", link:"https://sportsbook.fanduel.com", linkAndroid:"https://sportsbook.fanduel.com" },
  draftkings: { label:"DraftKings",     short:"DK",  c:"#30D158", link:"https://sportsbook.draftkings.com", linkAndroid:"https://sportsbook.draftkings.com" },
  betmgm:     { label:"BetMGM",         short:"MGM", c:"#FFD60A", link:"https://sports.betmgm.com", linkAndroid:"https://sports.betmgm.com" },
  caesars:    { label:"Caesars",        short:"CZR", c:"#BF5AF2", link:"https://sportsbook.caesars.com", linkAndroid:"https://sportsbook.caesars.com" },
};
const BO = ["hardrockbet","fanduel","draftkings","betmgm","caesars"];

const fmtO   = o => { const n=parseInt(String(o||"").replace(/\+/,"")); return isNaN(n)?"—":n>0?`+${n}`:`${n}`; };
const toImpl = o => { const n=parseInt(String(o||"").replace(/\+/,"")); if(isNaN(n))return 50; return n>0?(100/(n+100))*100:(Math.abs(n)/(Math.abs(n)+100))*100; };
const bestBook=(map,key)=>{ let b=-Infinity,bk=null; for(const[k,v]of Object.entries(map)){const n=parseInt(String(v[key]||"").replace(/\+/,"")); if(!isNaN(n)&&n>b){b=n;bk=k;}} return bk; };
const pad = n => String(n).padStart(2,"0");

const confStyle = c => ({
  ELITE:{ c:D.violet, bg:"rgba(191,90,242,.16)", b:"rgba(191,90,242,.4)", pct:100 },
  HIGH: { c:D.green,  bg:"rgba(48,209,88,.14)",  b:"rgba(48,209,88,.35)", pct:75  },
  MEDIUM:{ c:D.amber, bg:"rgba(255,159,10,.14)", b:"rgba(255,159,10,.35)",pct:50  },
  LOW:  { c:D.t2,     bg:"rgba(255,255,255,.06)",b:D.border,              pct:25  },
  PASS: { c:D.t3,     bg:"rgba(255,255,255,.04)",b:D.borderL,             pct:0   },
}[c]||{ c:D.t3, bg:"rgba(255,255,255,.04)", b:D.borderL, pct:0 });

const statusPriority = s => ({ live:0, scheduled:1, final:2 }[s] ?? 1);

const getSecsToOracle = () => {
  try {
    const et=new Date(new Date().toLocaleString("en-US",{timeZone:"America/New_York"}));
    const drop=new Date(et); drop.setHours(10,0,0,0);
    const diff=drop-et; return diff>0?Math.floor(diff/1000):0;
  } catch { return 0; }
};

// ── TEAM TREND ───────────────────────────────────────────────
// Compares 7-day vs 30-day runs scored/allowed to determine
// if a team is trending HOT, COLD, or NEUTRAL.
// Threshold: >0.75 RPG difference = meaningful trend
const teamTrend = (rs30, rs7, ra30, ra7) => {
  if (!rs30 || !rs7 || !ra30 || !ra7) return null;
  const offDelta = parseFloat(rs7)  - parseFloat(rs30); // + = scoring more
  const defDelta = parseFloat(ra30) - parseFloat(ra7);  // + = allowing fewer
  const combined = offDelta + defDelta;
  if (combined >  0.75) return 'HOT';
  if (combined < -0.75) return 'COLD';
  return 'NEUTRAL';
};

const formatTime = utc => {
  if(!utc) return "—";
  return new Date(utc).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit",timeZone:"America/New_York",timeZoneName:"short"});
};

const useCountdown = targetUtc => {
  const [secs,setSecs]=useState(()=>{ if(!targetUtc) return null; const diff=new Date(targetUtc)-new Date(); return diff>0?Math.floor(diff/1000):0; });
  useEffect(()=>{ if(secs===null||secs<=0) return; const id=setInterval(()=>setSecs(s=>s<=1?0:s-1),1000); return()=>clearInterval(id); },[secs]);
  if(secs===null||secs<=0) return null;
  const h=Math.floor(secs/3600),m=Math.floor((secs%3600)/60),s=secs%60;
  return h>0?`${h}h ${pad(m)}m`:`${m}:${pad(s)}`;
};

// ── STEP 6: wNarrative — suppress conflicting lean when model edge opposes ────
const wNarrative = (row, edgePct) => {
  if(!row) return "";
  if(row.wind_direction==="dome") return "Dome — weather neutral.";
  const ep = parseFloat(edgePct || 0);
  const p=[];
  if(row.wind_direction==="out"&&parseFloat(row.wind_mph||0)>=10){
    const lean = ep < 0 ? `${Math.round(row.wind_mph)}mph blowing out — ball carries. Wind accounted for in model projection.` : `${Math.round(row.wind_mph)}mph blowing out — ball carries. Lean over.`;
    p.push(lean);
  } else if(row.wind_direction==="in"&&parseFloat(row.wind_mph||0)>=10){
    const lean = ep > 0 ? `${Math.round(row.wind_mph)}mph blowing in — suppresses power. Wind accounted for in model projection.` : `${Math.round(row.wind_mph)}mph blowing in — suppresses power. Lean under.`;
    p.push(lean);
  }
  if(parseInt(row.rain_probability||0)>=40) p.push(`Rain ${row.rain_probability}% — delay risk.`);
  if(parseInt(row.temp_f||72)<50){
    const lean = ep > 0 ? `${row.temp_f}° — cold air, ball dies. Cold air accounted for in model projection.` : `${row.temp_f}° — cold air, ball dies. Lean under.`;
    p.push(lean);
  }
  return p.length?p.join(" "):`${row.temp_f||"—"}° and clear — neutral conditions.`;
};

// ── TOOLTIP CONTENT ────────────────────────────────────────────
const TIPS = {
  edge: {
    title: "What is Sharp Edge?",
    body: `Sharp Edge is the gap between our model's projected total and where the market has set the line — known as Closing Line Value (CLV) in betting circles.\n\nSharp bettors follow CLV because beating the closing line is the most reliable indicator of long-term profitability.\n\nAny edge above 4% is worth watching. Above 15%, the model has strong conviction. The Oracle only fires when edge and confidence align.`,
  },
  chaos: {
    title: "What is Chaos Score?",
    body: `Chaos measures how unpredictable today's run environment is — scored 0 to 100.\n\nCALM (0-24) — Neutral conditions. Weather is a non-factor, bullpens are fresh, and the market has this game priced efficiently. Trust the line.\n\nSTEADY (25-49) — Minor factors in play. Maybe a slight wind, moderate bullpen fatigue, or small line movement. Nothing extreme — bet the number, not the chaos.\n\nACTIVE (50-74) — Real volatility present. Wind, fatigue, or sharp money is creating a meaningful edge. Favor overs in active environments.\n\nCHAOS (75-100) — High unpredictability. Weather, tired bullpens, and market uncertainty all converging. Sharp bettors look for over value here — chaotic environments produce runs.\n\nFactors scored: wind direction/speed, rain delay probability, ballpark HR factor, umpire strikeout tendencies, bullpen fatigue, and sharp vs. public money splits.`,
  },
  confidence: {
    title: "Confidence Tiers",
    body: `ELITE (100%) — All 8 model factors aligned. Rare. Strong conviction play.\n\nHIGH (75%) — Strong signal across most factors. The Oracle fires here.\n\nMEDIUM (50%) — Decent lean. Watch for steam moves before pulling the trigger.\n\nLOW (25%) — Weak signal. Recreational play at best.\n\nPASS — No edge detected. The books have this one right — don't force it.`,
  },
  oracle: {
    title: "How the Oracle Works",
    body: `The Oracle is our lock of the day — one pick, when everything lines up.\n\nIt only fires when a game hits HIGH or ELITE confidence AND carries a meaningful edge AND the chaos score is in the right zone.\n\nWe'd rather go 0-for-1 than spray picks at everything on the board. Sharp bettors know: the worst habit in sports betting is chasing action. The Oracle is disciplined by design.`,
  },
};

const CSS = `
  *{box-sizing:border-box;margin:0;padding:0;-webkit-tap-highlight-color:transparent}
  html,body{background:#000;font-family:${SANS};color:#FFF}
  ::-webkit-scrollbar{width:4px;height:4px}
  ::-webkit-scrollbar-thumb{background:rgba(84,84,88,.5);border-radius:2px}
  @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn{from{opacity:0}to{opacity:1}}
  @keyframes pulse{0%,100%{opacity:1}50%{opacity:.15}}
  @keyframes shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
  @keyframes oracleIn{0%{opacity:0;transform:translateY(8px) scale(1.02)}100%{opacity:1;transform:translateY(0) scale(1)}}
  @keyframes lineGrow{from{width:0}to{width:100%}}
  @keyframes highlight{0%{box-shadow:0 0 0 0 rgba(10,132,255,.6)}70%{box-shadow:0 0 0 8px rgba(10,132,255,0)}100%{box-shadow:0 0 0 0 rgba(10,132,255,0)}}
  .card-enter{animation:fadeUp .38s cubic-bezier(.16,1,.3,1) both}
  .oracle-enter{animation:oracleIn .55s cubic-bezier(.16,1,.3,1) both}
  .game-highlight{animation:highlight 2s ease 3}
  .glass{background:rgba(28,28,30,.82);backdrop-filter:blur(24px) saturate(160%);-webkit-backdrop-filter:blur(24px) saturate(160%)}
  button:active{opacity:.7} a:active{opacity:.7}
  @media(min-width:1024px){
    .app-shell{display:grid;grid-template-columns:420px 1fr;grid-template-rows:auto 1fr;min-height:100vh}
    .top-nav{grid-column:1/-1}
    .left-col{overflow-y:auto;height:calc(100vh - 52px);padding:20px 20px 80px}
    .right-col{overflow-y:auto;height:calc(100vh - 52px);padding:20px 20px 80px;border-left:1px solid rgba(84,84,88,.35)}
    .mobile-nav{display:none!important}
    .desktop-nav-tabs{display:flex!important}
  }
  @media(max-width:1023px){
    .app-shell{display:block}
    .left-col,.right-col{padding:14px 14px 88px}
    .desktop-nav-tabs{display:none!important}
  }
`;

const Skel = ({h=14,w="100%",r=6})=>(
  <div style={{width:w,height:h,borderRadius:r,background:"linear-gradient(90deg,#1C1C1E 25%,#2C2C2E 50%,#1C1C1E 75%)",backgroundSize:"200% 100%",animation:"shimmer 1.4s infinite"}}/>
);

const ConfBadge = ({tier,size="sm",onTip})=>{
  const cs=confStyle(tier); const pct=cs.pct; const lg=size==="lg";
  return(
    <div onClick={onTip?e=>{e.stopPropagation();onTip(TIPS.confidence.title,TIPS.confidence.body);}:undefined}
      style={{display:"inline-flex",flexDirection:"column",gap:3,background:cs.bg,border:`1px solid ${cs.b}`,borderRadius:lg?10:8,padding:lg?"8px 12px":"4px 9px",minWidth:lg?100:72,cursor:onTip?"pointer":"default"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <span style={{fontSize:lg?11:8,fontWeight:700,color:cs.c,letterSpacing:.8,fontFamily:SANS}}>{tier||"—"}</span>
        {pct>0&&<span style={{fontSize:lg?12:9,fontWeight:700,color:cs.c,fontFamily:MONO}}>{pct}%</span>}
      </div>
      {pct>0&&(
        <div style={{height:2,background:"rgba(255,255,255,.1)",borderRadius:1,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:1,background:cs.c,width:`${pct}%`,transition:"width 1s cubic-bezier(.34,1.2,.64,1)"}}/>
        </div>
      )}
    </div>
  );
};

const Spark = ({data})=>{
  const nums=(data||[]).map(Number).filter(n=>!isNaN(n));
  if(nums.length<2) return null;
  const W=52,H=16,mn=Math.min(...nums)-.2,mx=Math.max(...nums)+.2;
  const pts=nums.map((n,i)=>[Math.round((i/(nums.length-1))*W),Math.round(H-((n-mn)/(mx-mn))*(H-3)-2)]);
  const d=pts.map((p,i)=>`${i===0?"M":"L"}${p[0]},${p[1]}`).join(" ");
  const col=nums[0]!==nums[nums.length-1]?D.amber:D.t3;
  return(
    <svg width={W} height={H} style={{display:"block",flexShrink:0,opacity:.9}}>
      <path d={d} fill="none" stroke={col} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx={pts[pts.length-1][0]} cy={pts[pts.length-1][1]} r={2} fill={col}/>
    </svg>
  );
};

const ChaosGauge = ({score,size=72,onTip})=>{
  const [on,setOn]=useState(false);
  useEffect(()=>{const t=setTimeout(()=>setOn(true),200);return()=>clearTimeout(t);},[]);
  const s=score||0; const CX=size/2,CY=size*.56,R=size*.36;
  const sA=Math.PI*.82,eA=Math.PI*2.18; const nA=sA+(s/100)*(eA-sA);
  const arc=(r,sa,ea)=>{const sx=CX+r*Math.cos(sa),sy=CY+r*Math.sin(sa),ex=CX+r*Math.cos(ea),ey=CY+r*Math.sin(ea);return`M${sx},${sy} A${r},${r} 0 ${ea-sa>Math.PI?1:0} 1 ${ex},${ey}`;};
  const col=s>=75?D.red:s>=50?D.amber:s>=25?D.gold:D.green;
  return(
    <div onClick={onTip?e=>{e.stopPropagation();onTip(TIPS.chaos.title,TIPS.chaos.body);}:undefined}
      style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1,cursor:onTip?"pointer":"default"}}>
      <svg width={size} height={size*.62} viewBox={`0 0 ${size} ${size*.62}`}>
        <path d={arc(R,sA,eA)} fill="none" stroke="rgba(84,84,88,.4)" strokeWidth={3} strokeLinecap="round"/>
        <path d={arc(R,sA,on?nA:sA)} fill="none" stroke={col} strokeWidth={3} strokeLinecap="round"
          style={{transition:"all 1.3s cubic-bezier(.34,1.56,.64,1)",filter:`drop-shadow(0 0 3px ${col}80)`}}/>
        <line x1={CX} y1={CY} x2={CX+R*Math.cos(on?nA:sA)} y2={CY+R*Math.sin(on?nA:sA)}
          stroke={col} strokeWidth={1.5} strokeLinecap="round" style={{transition:"all 1.3s cubic-bezier(.34,1.56,.64,1)"}}/>
        <circle cx={CX} cy={CY} r={2} fill={col}/>
        <text x={CX} y={CY-R*.42} textAnchor="middle" fill={col} fontSize={size*.145} fontWeight="700" fontFamily={MONO}>{s}</text>
      </svg>
      <span style={{fontSize:7,fontWeight:600,color:col,letterSpacing:1.2,fontFamily:SANS}}>
        {s>=75?"CHAOS":s>=50?"ACTIVE":s>=25?"STEADY":"CALM"}
      </span>
    </div>
  );
};

const OddsBar = ({book,odds,isBest})=>{
  const [w,setW]=useState(0);
  const bm=BOOKS[book]; const impl=toImpl(odds);
  useEffect(()=>{if(!bm)return;const t=setTimeout(()=>setW(impl),100);return()=>clearTimeout(t);},[impl,bm]);
  if(!bm) return null;
  const n=parseInt(String(odds||"").replace(/\+/,"")); const pos=!isNaN(n)&&n>0;
  return(
    <div style={{padding:"6px 0",borderBottom:`1px solid ${D.borderL}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
        <div style={{display:"flex",alignItems:"center",gap:6}}>
          <span style={{width:5,height:5,borderRadius:"50%",background:bm.c,display:"inline-block",flexShrink:0}}/>
          <span style={{fontSize:11,color:D.t2,fontFamily:SANS}}>{bm.label}</span>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:5}}>
          <span style={{fontSize:14,fontWeight:700,fontFamily:MONO,color:pos?D.green:D.t1}}>{fmtO(odds)}</span>
          {isBest&&<span style={{fontSize:7,fontWeight:700,background:D.blue,color:"#fff",borderRadius:20,padding:"1px 6px",letterSpacing:.3}}>BEST LINE</span>}
        </div>
      </div>
      <div style={{height:2,borderRadius:1,background:"rgba(84,84,88,.4)",overflow:"hidden"}}>
        <div style={{height:"100%",borderRadius:1,width:`${w}%`,transition:"width .65s cubic-bezier(.34,1.2,.64,1)",background:isBest?D.blue:D.t3,opacity:isBest?1:.5}}/>
      </div>
    </div>
  );
};

const BookLink = ({book,isAvailable})=>{
  const bm=BOOKS[book]; if(!bm) return null;
  const available=isAvailable(book);
  return(
    <div onClick={available?()=>{const isAndroid=/android/i.test(navigator.userAgent);window.open(isAndroid&&bm.linkAndroid?bm.linkAndroid:bm.link,"_blank","noopener,noreferrer");}:undefined}
      style={{display:"flex",alignItems:"center",gap:6,fontSize:10,fontWeight:700,color:available?bm.c:D.t3,border:`1px solid ${available?`${bm.c}25`:D.borderL}`,background:available?`${bm.c}0D`:"transparent",borderRadius:7,padding:"5px 11px",cursor:available?"pointer":"default",opacity:available?1:.45,transition:"opacity .15s"}}>
      {bm.short}{available?" ↗":"  ✗"}
    </div>
  );
};

const PitcherTank = ({pitcher,delay=0})=>{
  const [on,setOn]=useState(false);
  useEffect(()=>{const t=setTimeout(()=>setOn(true),delay+150);return()=>clearTimeout(t);},[delay]);
  const avg=pitcher.avg||90,cur=pitcher.current||0;
  const pct=Math.min((cur/avg)*100,120); const dpct=Math.min(pct,100);
  const col=pct<60?D.green:pct<85?D.amber:D.red;
  const status=pct<60?"FRESH":pct<85?"WORKING":"FADING";
  const W=200,H=100,CX=100,CY=95,R=72;
  const sA=Math.PI*.82,eA=Math.PI*2.18,tA=eA-sA; const fillA=sA+(dpct/100)*tA;
  const arc=(r,sa,ea)=>{const sx=CX+r*Math.cos(sa),sy=CY+r*Math.sin(sa),ex=CX+r*Math.cos(ea),ey=CY+r*Math.sin(ea);return`M${sx},${sy} A${r},${r} 0 ${ea-sa>Math.PI?1:0} 1 ${ex},${ey}`;};
  return(
    <div style={{background:D.s2,borderRadius:12,padding:"9px 11px",border:`1px solid ${pct>=85?`${col}40`:D.border}`}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:D.t1}}>{pitcher.name}</div>
          <div style={{fontSize:9,color:D.t2,marginTop:1}}>{pitcher.team} · {pitcher.side==="away"?"Away":"Home"}</div>
        </div>
        <div style={{textAlign:"right"}}>
          <span style={{fontSize:7,fontWeight:700,color:col,background:`${col}18`,border:`1px solid ${col}40`,borderRadius:20,padding:"2px 6px",letterSpacing:1}}>{status}</span>
          <div style={{fontSize:9,color:D.t2,marginTop:2}}>{pct>100?`${cur-avg} over limit`:`${Math.max(avg-cur,0)} pitches left`}</div>
        </div>
      </div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{maxHeight:68,overflow:"visible"}}>
        <path d={arc(R,sA,sA+tA*.6)} fill="none" stroke="rgba(48,209,88,.08)" strokeWidth={9} strokeLinecap="round"/>
        <path d={arc(R,sA+tA*.6,sA+tA*.85)} fill="none" stroke="rgba(255,159,10,.08)" strokeWidth={9} strokeLinecap="round"/>
        <path d={arc(R,sA+tA*.85,eA)} fill="none" stroke="rgba(255,69,58,.08)" strokeWidth={9} strokeLinecap="round"/>
        <path d={arc(R,sA,eA)} fill="none" stroke="rgba(84,84,88,.3)" strokeWidth={9} strokeLinecap="round"/>
        <path d={arc(R,sA,on?fillA:sA)} fill="none" stroke={col} strokeWidth={9} strokeLinecap="round"
          style={{transition:"all 1.2s cubic-bezier(.34,1.2,.64,1)",filter:`drop-shadow(0 0 6px ${col}50)`}}/>
        <text x={CX} y={CY-28} textAnchor="middle" fill={col} fontSize={24} fontWeight="700" fontFamily={MONO}>{on?cur:0}</text>
        <text x={CX} y={CY-12} textAnchor="middle" fill={D.t3} fontSize={9} fontFamily={SANS}>pitches</text>
      </svg>
      <div style={{display:"flex",justifyContent:"space-between",marginTop:-4}}>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:7,color:D.t2,letterSpacing:.8}}>NOW</div>
          <div style={{fontSize:13,fontWeight:700,color:D.t1,fontFamily:MONO}}>{cur}</div>
        </div>
        <div style={{textAlign:"center"}}>
          <div style={{fontSize:7,color:D.t2,letterSpacing:.8}}>AVG LIMIT</div>
          <div style={{fontSize:13,fontWeight:700,color:D.t2,fontFamily:MONO}}>{avg}</div>
        </div>
      </div>
      <div style={{height:3,background:"rgba(84,84,88,.3)",borderRadius:2,overflow:"hidden",marginTop:6}}>
        <div style={{height:"100%",width:on?`${Math.min(pct,100)}%`:"0%",borderRadius:2,background:`linear-gradient(90deg,${D.green},${D.amber} 60%,${D.red})`,transition:"width 1.2s cubic-bezier(.34,1.2,.64,1)"}}/>
      </div>
    </div>
  );
};

const HRCard = ({batter,delay=0})=>{
  const [on,setOn]=useState(false);
  useEffect(()=>{const t=setTimeout(()=>setOn(true),delay+200);return()=>clearTimeout(t);},[delay]);
  const due=batter.due||0; const adjDue=batter.adjusted_due||batter.due||0;
  const showAdj=batter.adjusted_due&&batter.adjusted_due!==batter.due;
  const col=adjDue>=90?D.red:adjDue>=65?D.amber:adjDue>=40?D.gold:D.green;
  const label=adjDue>=130?"OVERDUE":adjDue>=100?"DUE NOW":"APPROACHING";
  const isIL = batter.is_active === false;
  const ilStatus = batter.roster_status || 'IL';

  return(
    <div style={{background:D.s2,borderRadius:12,padding:"11px 13px",border:`1px solid ${isIL?`${D.red}25`:adjDue>=65?`${col}30`:D.borderL}`,boxShadow:adjDue>=90&&!isIL?`0 2px 16px ${col}15`:"none",opacity:isIL?0.55:1}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div>
          <div style={{display:"flex",alignItems:"center",gap:6}}>
            <div style={{fontSize:13,fontWeight:600,color:D.t1}}>{batter.name}</div>
            {isIL&&(
              <span style={{fontSize:7,fontWeight:700,color:D.red,background:`${D.red}18`,border:`1px solid ${D.red}40`,borderRadius:20,padding:"2px 6px",letterSpacing:.8}}>
                {ilStatus}
              </span>
            )}
            {/* STEP 5: HOT badge — SLG >= 0.576 (120% of 0.480 baseline) */}
            {!isIL&&batter.slugging_pct>=0.576&&(
              <span style={{fontSize:7,fontWeight:700,color:D.green,background:`${D.green}18`,border:`1px solid ${D.green}35`,borderRadius:20,padding:"1px 6px",letterSpacing:.8}}>HOT</span>
            )}
            {/* STEP 5: COLD badge — SLG <= 0.360 (75% of 0.480 baseline) */}
            {!isIL&&batter.slugging_pct>0&&batter.slugging_pct<=0.360&&(
              <span style={{fontSize:7,fontWeight:700,color:D.cyan,background:`${D.cyan}18`,border:`1px solid ${D.cyan}35`,borderRadius:20,padding:"1px 6px",letterSpacing:.8}}>COLD</span>
            )}
          </div>
          {/* STEP 5: BA · SLG stat line — only shown when batting_avg > 0 */}
          <div style={{fontSize:10,color:D.t2,marginTop:1}}>
            {batter.team} · {batter.hr} HR · {batter.games} GP
            {batter.batting_avg>0&&(
              <span style={{marginLeft:6,color:D.t3}}>{parseFloat(batter.batting_avg).toFixed(3)} BA · {parseFloat(batter.slugging_pct).toFixed(3)} SLG</span>
            )}
          </div>
        </div>
        <div style={{textAlign:"right"}}>
          {isIL
            ? <span style={{fontSize:8,color:D.t3}}>unavailable</span>
            : <span style={{fontSize:7,fontWeight:700,color:col,background:`${col}18`,border:`1px solid ${col}35`,borderRadius:20,padding:"2px 8px",letterSpacing:1}}>{label}</span>
          }
          <div style={{fontSize:10,color:D.t2,marginTop:3}}>{batter.since}g since last HR</div>
        </div>
      </div>
      <div style={{marginBottom:3}}>
        <div style={{fontSize:8,color:D.t2,marginBottom:3,letterSpacing:.5}}>BASE RATE <span style={{fontWeight:400,letterSpacing:0}}>— how overdue based on his season avg</span></div>
        <div style={{position:"relative",height:5,background:"rgba(84,84,88,.3)",borderRadius:3,overflow:"hidden"}}>
          <div style={{position:"absolute",left:"65%",top:0,bottom:0,width:1,background:"rgba(255,159,10,.4)"}}/>
          <div style={{position:"absolute",left:"90%",top:0,bottom:0,width:1,background:"rgba(255,69,58,.4)"}}/>
          <div style={{position:"absolute",left:0,top:0,bottom:0,borderRadius:3,width:on?`${Math.min(due,100)}%`:"0%",background:`linear-gradient(90deg,${D.green},${D.gold} 45%,${D.amber} 65%,${D.red} 90%)`,transition:"width 1s cubic-bezier(.34,1.2,.64,1)"}}/>
        </div>
      </div>
      {showAdj&&(
        <div style={{marginBottom:6}}>
          <div style={{fontSize:8,color:D.t3,marginBottom:3,letterSpacing:.5}}>
            TODAY'S ODDS <span style={{fontWeight:400,letterSpacing:0}}>— adjusted for pitcher, park, weather & form</span>{batter.opp_pitcher?` · vs ${batter.opp_pitcher.split(" ").pop()}${batter.opp_hand?` (${batter.opp_hand}HP)`:""}`:""}{batter.venue?` · ${batter.venue}`:""}
          </div>
          <div style={{position:"relative",height:5,background:"rgba(84,84,88,.3)",borderRadius:3,overflow:"hidden"}}>
            <div style={{position:"absolute",left:"65%",top:0,bottom:0,width:1,background:"rgba(255,159,10,.4)"}}/>
            <div style={{position:"absolute",left:"90%",top:0,bottom:0,width:1,background:"rgba(255,69,58,.4)"}}/>
            <div style={{position:"absolute",left:0,top:0,bottom:0,borderRadius:3,width:on?`${Math.min(adjDue,100)}%`:"0%",background:`linear-gradient(90deg,${D.green},${D.gold} 45%,${D.amber} 65%,${D.red} 90%)`,boxShadow:adjDue>=65?`0 0 8px ${col}60`:"none",transition:"width 1s cubic-bezier(.34,1.2,.64,1) .1s"}}/>
          </div>
        </div>
      )}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:10,color:D.t2}}>Avg HR every <span style={{color:D.t2,fontWeight:600,fontFamily:MONO}}>{batter.avg_between?.toFixed(1)||"—"}</span>g</div>
        <div style={{display:"flex",alignItems:"baseline",gap:4}}>
          {showAdj&&<span style={{fontSize:11,color:D.t3,fontFamily:MONO,textDecoration:"line-through"}}>{on?due:0}%</span>}
          <span style={{fontSize:15,fontWeight:700,color:col,fontFamily:MONO}}>{on?adjDue:0}</span>
          <span style={{fontSize:9,color:col}}>%</span>
        </div>
      </div>
    </div>
  );
};

const BaseDiamond = ({onFirst,onSecond,onThird,size=80})=>{
  const CX=size/2,CY=size/2,r=size*.24;
  const bases=[{x:CX+r,y:CY,on:onFirst},{x:CX,y:CY-r,on:onSecond},{x:CX-r,y:CY,on:onThird},{x:CX,y:CY+r,on:false}];
  return(
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <polygon points={`${CX+r},${CY} ${CX},${CY-r} ${CX-r},${CY} ${CX},${CY+r}`} fill="none" stroke="rgba(84,84,88,.5)" strokeWidth={1}/>
      {bases.map((b,i)=>(
        <g key={i}>
          <rect x={b.x-7} y={b.y-7} width={14} height={14} rx={2} fill={b.on?D.amber:"rgba(84,84,88,.2)"} stroke={b.on?D.amber:"rgba(84,84,88,.5)"} strokeWidth={1} transform={`rotate(45 ${b.x} ${b.y})`} style={{transition:"fill .3s,stroke .3s"}}/>
          {b.on&&<circle cx={b.x} cy={b.y} r={3} fill={D.amber} opacity={.9}/>}
        </g>
      ))}
    </svg>
  );
};

// ── LIVE GAME CARD ─────────────────────────────────────────────
const LiveGameCard = React.memo(({game,batters})=>{
  const [open,setOpen]=useState(true);
  const isLive=game.inning!=null&&game.away_runs!=null;
  const rawPitchers = useGamePitchers(game.game_id);
  const pitchers = ["away","home"].reduce((acc,side)=>{
    const sp = rawPitchers.filter(p=>p.side===side);
    const p  = sp.find(p=>p.is_active) || sp.find(p=>p.is_starter);
    if(p) acc.push({name:p.pitcher_name,team:p.side==="away"?game.away_abbr:game.home_abbr,side:p.side,avg:p.avg_pitch_limit||90,current:p.pitch_count||0});
    return acc;
  },[]);

  return(
    <div style={{background:D.s1,borderRadius:16,overflow:"hidden",marginBottom:12,border:`1px solid ${isLive?"rgba(48,209,88,.25)":D.border}`}}>
      <div style={{padding:"14px 16px",cursor:"pointer"}} onClick={()=>setOpen(o=>!o)}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {isLive&&<span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite",display:"inline-block",flexShrink:0}}>●</span>}
            <span style={{fontSize:22,fontWeight:700,letterSpacing:-.5,color:D.t1}}>
              {game.away_abbr}<span style={{color:D.t2,fontWeight:300,margin:"0 6px",fontSize:18}}>@</span>{game.home_abbr}
            </span>
          </div>
          <div style={{textAlign:"right"}}>
            {isLive?(
              <div>
                <div style={{fontSize:13,fontWeight:700,color:D.live,fontFamily:MONO}}>{game.away_runs??0} — {game.home_runs??0}</div>
                <div style={{fontSize:10,color:D.t2}}>{game.inning_half} {game.inning}</div>
              </div>
            ):(
              <div style={{fontSize:14,color:D.blue,fontWeight:600,fontFamily:MONO}}>{formatTime(game.scheduled_time)}</div>
            )}
          </div>
        </div>
        {isLive&&(
          <div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap"}}>
            <div style={{display:"flex",gap:8,fontSize:11,color:D.t2}}>
              <span>⚾ {game.balls??0}-{game.strikes??0}</span><span>·</span>
              <span>{game.outs??0} out{game.outs!==1?"s":""}</span>
            </div>
            <BaseDiamond onFirst={game.on_first} onSecond={game.on_second} onThird={game.on_third} size={52}/>
            {game.current_batter&&(
              <div style={{fontSize:10,color:D.t2}}>
                <div>🏏 {game.current_batter}</div>
                {game.current_pitcher&&<div>⚾ {game.current_pitcher}</div>}
              </div>
            )}
          </div>
        )}
      </div>
      {open&&(
        <div style={{animation:"fadeIn .2s ease"}}>
          {isLive&&(
            <div style={{margin:"0 16px 10px",display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6,textAlign:"center",background:D.s2,borderRadius:10,padding:"10px",border:`1px solid ${D.borderL}`}}>
              {[{label:game.away_abbr,runs:game.away_runs,hits:game.away_hits,errors:game.away_errors},{label:"",runs:null},{label:game.home_abbr,runs:game.home_runs,hits:game.home_hits,errors:game.home_errors}].map((t,i)=>i===1?(
                <div key={i} style={{display:"flex",flexDirection:"column",justifyContent:"center"}}><div style={{fontSize:8,color:D.t2}}>R  H  E</div></div>
              ):(
                <div key={i}>
                  <div style={{fontSize:10,color:D.t2,marginBottom:3}}>{t.label}</div>
                  <div style={{display:"flex",justifyContent:"center",gap:10}}>
                    <span style={{fontSize:20,fontWeight:700,color:D.t1,fontFamily:MONO}}>{t.runs??0}</span>
                    <span style={{fontSize:14,color:D.t2,fontFamily:MONO,alignSelf:"flex-end",marginBottom:2}}>{t.hits??0}</span>
                    <span style={{fontSize:14,color:t.errors>0?D.red:D.t3,fontFamily:MONO,alignSelf:"flex-end",marginBottom:2}}>{t.errors??0}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isLive&&game.last_play&&(
            <div key={game.last_play} style={{margin:"0 16px 10px",padding:"8px 12px",background:D.s2,borderRadius:8,fontSize:11,color:D.t2,fontStyle:"italic",border:`1px solid ${D.borderL}`,animation:"fadeIn .4s ease"}}>{game.last_play}</div>
          )}
          {pitchers.length>0&&(
            <div style={{padding:"0 16px 12px"}}>
              <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.2,marginBottom:8}}>PITCHER TANK</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {pitchers.map((p,i)=><PitcherTank key={`${p.name}-${i}`} pitcher={p} delay={i*100}/>)}
              </div>
            </div>
          )}
          {batters?.length>0&&(
            <div style={{padding:"0 16px 14px"}}>
              <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.2,marginBottom:8}}>HR WATCH</div>
              <div style={{display:"flex",flexDirection:"column",gap:7}}>
                {batters.slice(0,3).map((b,i)=><HRCard key={i} batter={b} delay={i*60}/>)}
              </div>
            </div>
          )}
        </div>
      )}
      <div onClick={()=>setOpen(o=>!o)} style={{textAlign:"center",padding:"6px",borderTop:`1px solid ${D.borderL}`,fontSize:9,color:D.t2,cursor:"pointer",letterSpacing:1}}>{open?"▲":"▼"}</div>
    </div>
  );
});

// ── ORACLE SHARE CARD ─────────────────────────────────────────
const OracleShareCard = ({ oracle, onClose }) => {
  const canvasRef = useRef(null);
  const [drawn, setDrawn] = useState(false);

  const rr = (ctx, x, y, w, h, r) => {
    ctx.beginPath();
    ctx.moveTo(x+r, y);
    ctx.lineTo(x+w-r, y); ctx.arcTo(x+w,y,x+w,y+r,r);
    ctx.lineTo(x+w,y+h-r); ctx.arcTo(x+w,y+h,x+w-r,y+h,r);
    ctx.lineTo(x+r,y+h); ctx.arcTo(x,y+h,x,y+h-r,r);
    ctx.lineTo(x,y+r); ctx.arcTo(x,y,x+r,y,r);
    ctx.closePath();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !oracle) return;
    const W = 600, H = 620, SC = 2;
    canvas.width  = W * SC;
    canvas.height = H * SC;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(SC, SC);

    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#0A0A14');
    bg.addColorStop(.55, '#0D1230');
    bg.addColorStop(1, '#050508');
    ctx.fillStyle = bg; rr(ctx,0,0,W,H,20); ctx.fill();

    const glow = ctx.createRadialGradient(W/2,-20,0,W/2,-20,320);
    glow.addColorStop(0,'rgba(90,200,250,.1)'); glow.addColorStop(1,'rgba(90,200,250,0)');
    ctx.fillStyle = glow; rr(ctx,0,0,W,H,20); ctx.fill();

    ctx.strokeStyle = 'rgba(90,200,250,.22)'; ctx.lineWidth = 1.5;
    rr(ctx,1,1,W-2,H-2,19); ctx.stroke();

    ctx.textAlign = 'center';
    ctx.font = '700 10px -apple-system,BlinkMacSystemFont,sans-serif';
    ctx.fillStyle = '#5AC8FA';
    ctx.fillText('⚡  T H E   O R A C L E', W/2, 52);

    const ds = oracle.date
      ? new Date(oracle.date+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}).toUpperCase()
      : new Date().toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'}).toUpperCase();
    ctx.font = '400 10px -apple-system,sans-serif';
    ctx.fillStyle = 'rgba(235,235,245,.35)';
    ctx.fillText(ds, W/2, 70);

    const div1 = ctx.createLinearGradient(60,0,W-60,0);
    div1.addColorStop(0,'transparent'); div1.addColorStop(.3,'rgba(90,200,250,.35)');
    div1.addColorStop(.7,'rgba(90,200,250,.35)'); div1.addColorStop(1,'transparent');
    ctx.strokeStyle = div1; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(60,84); ctx.lineTo(W-60,84); ctx.stroke();

    ctx.font = '700 44px -apple-system,sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(`${oracle.away_abbr}  @  ${oracle.home_abbr}`, W/2, 148);

    if (oracle.venue_name) {
      ctx.font = '400 11px -apple-system,sans-serif';
      ctx.fillStyle = 'rgba(235,235,245,.35)';
      ctx.fillText(oracle.venue_name, W/2, 168);
    }

    ctx.font = '700 68px -apple-system,sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText(oracle.pick, W/2, 264);

    const bm = BOOKS[oracle.book];
    const oddsStr = oracle.odds > 0 ? `+${oracle.odds}` : `${oracle.odds}`;
    ctx.font = '700 26px -apple-system,sans-serif';
    ctx.fillStyle = '#30D158';
    const oddsW = ctx.measureText(oddsStr).width;
    const atStr = bm ? `@ ${bm.label}` : `@ ${(oracle.book||'').toUpperCase()}`;
    ctx.font = '400 13px -apple-system,sans-serif';
    const atW = ctx.measureText(atStr).width;
    const totalW = oddsW + 10 + atW;
    ctx.font = '700 26px -apple-system,sans-serif';
    ctx.fillStyle = '#30D158';
    ctx.textAlign = 'left';
    ctx.fillText(oddsStr, W/2 - totalW/2, 302);
    ctx.font = '400 13px -apple-system,sans-serif';
    ctx.fillStyle = 'rgba(235,235,245,.5)';
    ctx.fillText(atStr, W/2 - totalW/2 + oddsW + 10, 302);
    ctx.textAlign = 'center';

    const confC = {ELITE:'#BF5AF2',HIGH:'#30D158',MEDIUM:'#FF9F0A',LOW:'rgba(235,235,245,.4)',PASS:'rgba(235,235,245,.2)'}[oracle.confidence_tier] || '#5AC8FA';
    const chaosN = oracle.chaos_total || 0;
    const chaosC = chaosN >= 75 ? '#FF453A' : chaosN >= 50 ? '#FF9F0A' : chaosN >= 25 ? '#FFD60A' : '#30D158';
    const ep = parseFloat(oracle.edge_pct || 0);
    const stats = [
      { label:'SHARP EDGE', value: ep > 0 ? `+${ep.toFixed(1)}%` : `${ep.toFixed(1)}%`, color:'#5AC8FA' },
      { label:'CONFIDENCE', value: oracle.confidence_tier || '—', color: confC },
      { label:'CHAOS', value: `${chaosN} / 100`, color: chaosC },
    ];
    const bw = (W - 80) / 3;
    stats.forEach(({ label, value, color }, i) => {
      const bx = 40 + i * bw, by = 328, bh = 70;
      ctx.fillStyle = 'rgba(255,255,255,.04)';
      ctx.strokeStyle = 'rgba(84,84,88,.3)'; ctx.lineWidth = 1;
      rr(ctx, bx+2, by, bw-8, bh, 10); ctx.fill(); ctx.stroke();
      const cx = bx + bw/2 - 2;
      ctx.font = '600 8px -apple-system,sans-serif';
      ctx.fillStyle = 'rgba(235,235,245,.3)';
      ctx.fillText(label, cx, by + 20);
      ctx.font = `700 ${value.length > 6 ? 17 : 21}px -apple-system,sans-serif`;
      ctx.fillStyle = color;
      ctx.fillText(value, cx, by + 50);
    });

    if (oracle.rationale) {
      const maxW = W - 100;
      ctx.font = '400 12px -apple-system,sans-serif';
      ctx.fillStyle = 'rgba(235,235,245,.45)';
      const words = oracle.rationale.split(' ');
      let line = '', y = 432, lines = 0;
      for (const word of words) {
        const test = line + word + ' ';
        if (ctx.measureText(test).width > maxW && line) {
          if (lines < 3) { ctx.fillText(line.trim(), W/2, y); y += 19; lines++; }
          else { ctx.fillText(line.trim().replace(/.{3}$/, '…'), W/2, y); break; }
          line = word + ' ';
        } else { line = test; }
      }
      if (line && lines < 3) ctx.fillText(line.trim(), W/2, y);
    }

    const fy = H - 42;
    const fdiv = ctx.createLinearGradient(40,0,W-40,0);
    fdiv.addColorStop(0,'transparent'); fdiv.addColorStop(.15,'rgba(84,84,88,.4)');
    fdiv.addColorStop(.85,'rgba(84,84,88,.4)'); fdiv.addColorStop(1,'transparent');
    ctx.strokeStyle = fdiv; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(40,fy); ctx.lineTo(W-40,fy); ctx.stroke();

    ctx.textAlign = 'left';
    ctx.font = '700 14px -apple-system,sans-serif';
    ctx.fillStyle = '#FFFFFF';
    ctx.fillText('SHARP', 44, fy+20);
    ctx.fillStyle = '#5AC8FA';
    ctx.fillText('INNING', 44 + ctx.measureText('SHARP').width + 3, fy+20);

    ctx.textAlign = 'right';
    ctx.font = '400 10px -apple-system,sans-serif';
    ctx.fillStyle = 'rgba(235,235,245,.25)';
    ctx.fillText('21+  ·  Gamble Responsibly  ·  sharpinning.com', W-44, fy+20);

    setDrawn(true);
  }, [oracle]);

  const download = () => {
    const a = document.createElement('a');
    a.href = canvasRef.current.toDataURL('image/png');
    a.download = `sharpinning-oracle-${oracle?.date||'pick'}.png`;
    a.click();
  };

  const shareNative = () => {
    canvasRef.current.toBlob(async blob => {
      const file = new File([blob], 'sharpinning-oracle.png', { type:'image/png' });
      try {
        if (navigator.canShare?.({ files:[file] })) {
          await navigator.share({ files:[file], title:`SharpInning Oracle · ${oracle?.pick}`, text:`${oracle?.away_abbr}@${oracle?.home_abbr} ${oracle?.pick} · Edge ${oracle?.edge_pct>0?'+':''}${parseFloat(oracle?.edge_pct||0).toFixed(1)}% · sharpinning.com` });
        } else { download(); }
      } catch { download(); }
    });
  };

  return (
    <div onClick={onClose}
      style={{position:'fixed',inset:0,background:'rgba(0,0,0,.88)',zIndex:400,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div onClick={e=>e.stopPropagation()}
        style={{display:'flex',flexDirection:'column',alignItems:'center',gap:12,animation:'fadeUp .3s cubic-bezier(.16,1,.3,1)'}}>
        <canvas ref={canvasRef}
          style={{borderRadius:12,maxWidth:'100%',maxHeight:'70vh',display:'block',opacity:drawn?1:0,transition:'opacity .3s'}}/>
        {drawn&&(
          <div style={{display:'flex',gap:8,width:'100%'}}>
            <button onClick={shareNative}
              style={{flex:1,padding:'11px',borderRadius:12,border:'none',background:'#5AC8FA',color:'#000',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:SANS}}>
              ↑ Share Image
            </button>
            <button onClick={download}
              style={{flex:1,padding:'11px',borderRadius:12,border:`1px solid ${D.border}`,background:'transparent',color:D.t1,fontSize:13,fontWeight:600,cursor:'pointer',fontFamily:SANS}}>
              ⬇ Download
            </button>
          </div>
        )}
        <button onClick={onClose}
          style={{width:'100%',padding:'9px',borderRadius:12,border:`1px solid ${D.borderL}`,background:'transparent',color:D.t2,fontSize:12,cursor:'pointer',fontFamily:SANS}}>
          Close
        </button>
      </div>
    </div>
  );
};

// ── ORACLE HERO ────────────────────────────────────────────────
const OracleHero = ({oracle,boardGames,onTip,isAvailable,trackRecord,liveGames})=>{
  const [secs,setSecs]=useState(getSecsToOracle());
  const [revealed,setRevealed]=useState(secs===0);
  const [rotation,setRotation]=useState(0);
  const [demo,setDemo]=useState(false);
  const [showCard,setShowCard]=useState(false);
  const skipAnim=useRef(false);

  useEffect(()=>{const last=localStorage.getItem("si_visit");if(last&&Date.now()-parseInt(last)<4*3600000)skipAnim.current=true;localStorage.setItem("si_visit",String(Date.now()));},[]);
  useEffect(()=>{if(secs<=0){setRevealed(true);return;}const id=setInterval(()=>setSecs(s=>{if(s<=1){setRevealed(true);clearInterval(id);return 0;}return s-1;}),1000);return()=>clearInterval(id);},[]);// eslint-disable-line
  useEffect(()=>{if(oracle)return;const id=setInterval(()=>setRotation(r=>(r+1)%3),8000);return()=>clearInterval(id);},[oracle]);

  const show=(revealed||oracle)&&!demo;
  const h=Math.floor(secs/3600),m=Math.floor((secs%3600)/60),s=secs%60;
  const bm=BOOKS[oracle?.book];

  const oracleGame=oracle?.game_id?(liveGames||[]).find(g=>String(g.game_id)===String(oracle.game_id)):null;
  const isLive=oracleGame?.status==="live"||oracleGame?.inning!=null;
  const isFinal=oracle?.result&&oracle.result!=="PENDING";

  const todayStr=new Date().toISOString().slice(0,10);
  const bestGame=boardGames?.filter(g=>(g.status==="scheduled"||g.status==="live")&&g.date===todayStr&&parseFloat(g.edge_pct||0)>4&&g.confidence_tier!=="PASS"&&g.confidence_tier!=="LOW"&&g.confidence_tier!=null).sort((a,b)=>parseFloat(b.edge_pct)-parseFloat(a.edge_pct))[0];
  const bestGameLive=bestGame?.game_id?(liveGames||[]).find(g=>String(g.game_id)===String(bestGame.game_id)):null;
  const bestIsLive=bestGameLive?.status==="live"||bestGameLive?.inning!=null;
  const bestIsFinal=bestGameLive?.status==="final";
  const totalGames=boardGames?.length||0;
  const maxChaos=boardGames?.length?Math.max(...boardGames.map(g=>g.chaos_total||0)):0;
  const rainGames=boardGames?.filter(g=>parseInt(g.rain_probability||0)>=40).length||0;

  const fallbackContent=[
    bestGame?(
      <div key="spd">
        <div style={{fontSize:9,fontWeight:600,color:D.blue,letterSpacing:2,marginBottom:8}}>SHARP PICK OF THE DAY</div>
        <div style={{display:"flex",alignItems:"baseline",gap:12,marginBottom:6,flexWrap:"wrap"}}>
          <div style={{fontSize:36,fontWeight:700,color:D.t1,letterSpacing:-.5}}>{bestGame.away_abbr}@{bestGame.home_abbr}</div>
          <div>
            <div style={{fontSize:13,color:D.t2}}>OVER {bestGame.market_line}</div>
            <div style={{fontSize:11,fontWeight:600,color:D.blue}}>Edge +{parseFloat(bestGame.edge_pct).toFixed(1)}%</div>
          </div>
        </div>
        <div style={{fontSize:11,color:D.t2,marginBottom:6}}>Model projects {bestGame.projected_total} runs · {formatTime(bestGame.scheduled_time)}</div>
        <ConfBadge tier={bestGame.confidence_tier} onTip={onTip}/>
      </div>
    ):(
      <div key="nopick">
        <div style={{fontSize:9,fontWeight:600,color:D.blue,letterSpacing:2,marginBottom:8}}>SHARP PICK OF THE DAY</div>
        <div style={{fontSize:13,color:D.t2}}>Model analyzing today's slate — sharp angles surface after 10am ET.</div>
      </div>
    ),
    <div key="slate">
      <div style={{fontSize:9,fontWeight:600,color:D.blue,letterSpacing:2,marginBottom:10}}>TODAY'S SLATE SUMMARY</div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        {[{v:totalGames,l:"Games",c:D.t1},{v:maxChaos,l:"Peak Chaos",c:maxChaos>=60?D.amber:D.green},{v:rainGames,l:"Rain Risk",c:rainGames>0?D.amber:D.green}].map(({v,l,c})=>(
          <div key={l} style={{textAlign:"center",background:D.s2,borderRadius:10,padding:"10px 6px",border:`1px solid ${D.borderL}`}}>
            <div style={{fontSize:26,fontWeight:700,color:c,fontFamily:MONO,lineHeight:1}}>{v}</div>
            <div style={{fontSize:9,color:D.t2,marginTop:3,letterSpacing:.5}}>{l.toUpperCase()}</div>
          </div>
        ))}
      </div>
    </div>,
    <div key="insight">
      <div style={{fontSize:9,fontWeight:600,color:D.blue,letterSpacing:2,marginBottom:8}}>SHARP INSIGHT</div>
      {maxChaos>=60?(
        <div>
          <div style={{fontSize:18,fontWeight:700,color:D.amber,marginBottom:4}}>⚡ High Chaos Slate</div>
          <div style={{fontSize:14,color:D.t2,lineHeight:1.7}}>Peak chaos at {maxChaos}/100 today. Sharp bettors know high chaos environments create volatile scoring — weather, fatigue, and market uncertainty push totals over the closing line.</div>
        </div>
      ):(
        <div>
          <div style={{fontSize:18,fontWeight:700,color:D.green,marginBottom:4}}>📊 Clean Slate</div>
          <div style={{fontSize:14,color:D.t2,lineHeight:1.7}}>Low chaos across the board. Model projections carry more weight when conditions are stable. Fade the public and trust the closing line value on high-edge games.</div>
        </div>
      )}
    </div>,
  ];

  return(
    <div className={skipAnim.current?"":"oracle-enter"}
      style={{position:"relative",overflow:"hidden",borderRadius:18,marginBottom:14,background:"linear-gradient(140deg,#0A0A14 0%,#0D1230 55%,#050508 100%)",border:"1px solid rgba(90,200,250,.18)"}}>
      <div style={{position:"absolute",top:-60,left:"50%",transform:"translateX(-50%)",width:260,height:160,background:"radial-gradient(ellipse,rgba(90,200,250,.12) 0%,transparent 70%)",pointerEvents:"none"}}/>
      <div style={{position:"relative",padding:"18px 18px 16px"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",gap:7}}>
            <span onClick={()=>onTip&&onTip(TIPS.oracle.title,TIPS.oracle.body)} style={{fontSize:9,fontWeight:700,color:D.oracle,letterSpacing:2.5,cursor:"pointer"}}>⚡ THE ORACLE</span>
            <span style={{fontSize:8,color:D.t2,letterSpacing:.8}}>DAILY SHARP PICK</span>
          </div>
          <button onClick={()=>setDemo(d=>!d)} style={{fontSize:10,color:D.t2,background:"transparent",border:`1px solid ${D.borderL}`,borderRadius:8,padding:"3px 9px",cursor:"pointer",fontFamily:SANS}}>
            {show?"Timer":"Reveal →"}
          </button>
        </div>
        {!show?(
          <div>
            <div style={{fontSize:9,color:D.t2,letterSpacing:2,marginBottom:10,textAlign:"center"}}>TODAY'S LOCK DROPS IN</div>
            <div style={{display:"flex",justifyContent:"center",gap:3,marginBottom:12}}>
              {[{v:h,l:"HRS"},{v:m,l:"MIN"},{v:s,l:"SEC"}].map(({v,l},i)=>(
                <div key={l} style={{display:"flex",alignItems:"center",gap:3}}>
                  {i>0&&<span style={{fontSize:32,color:D.t2,fontWeight:700,lineHeight:1,fontFamily:MONO,marginTop:-2}}>:</span>}
                  <div style={{textAlign:"center"}}>
                    <div style={{fontSize:44,fontWeight:700,color:D.t1,lineHeight:1,fontFamily:MONO,letterSpacing:-2,minWidth:56}}>{pad(v)}</div>
                    <div style={{fontSize:7,color:D.t2,letterSpacing:2,marginTop:2}}>{l}</div>
                  </div>
                </div>
              ))}
            </div>
            {trackRecord&&trackRecord.length>0&&(()=>{
              const settled=trackRecord.filter(r=>r.result&&r.result!=="PENDING").slice(0,5);
              const allSettled=trackRecord.filter(r=>r.result&&r.result!=="PENDING");
              const wins=allSettled.filter(r=>r.result==="WIN").length;
              const losses=allSettled.filter(r=>r.result==="LOSS").length;
              const totalPnl=allSettled.reduce((s,r)=>s+parseFloat(r.pnl_units||0),0);
              if(!settled.length) return null;
              return(
                <div style={{borderTop:`1px solid ${D.borderL}`,padding:"10px 0 4px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:7,fontWeight:700,color:D.t3,letterSpacing:1.5}}>RECENT ORACLE RESULTS</div>
                    <div style={{fontSize:10,fontWeight:700,color:totalPnl>=0?D.green:D.red,fontFamily:MONO}}>
                      {wins}-{losses} · {totalPnl>=0?"+":""}{totalPnl.toFixed(1)}u
                    </div>
                  </div>
                  {settled.map((r,i)=>{
                    const win=r.result==="WIN";
                    const push=r.result==="PUSH";
                    const col=win?D.green:push?D.amber:D.red;
                    const pnl=parseFloat(r.pnl_units||0);
                    return(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 0",borderBottom:i<settled.length-1?`1px solid ${D.t4}`:""}} >
                        <div style={{fontSize:8,color:D.t3,width:36,flexShrink:0,fontFamily:MONO}}>
                          {new Date(r.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                        </div>
                        <div style={{flex:1,fontSize:10,fontWeight:600,color:D.t1}}>{r.pick}</div>
                        <div style={{fontSize:7,fontWeight:700,color:col,background:`${col}15`,border:`1px solid ${col}30`,borderRadius:20,padding:"2px 7px",letterSpacing:.5}}>
                          {r.result}
                        </div>
                        <div style={{fontSize:9,fontWeight:700,color:col,fontFamily:MONO,width:38,textAlign:"right"}}>
                          {pnl>=0?"+":""}{pnl.toFixed(2)}u
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            <div style={{borderTop:`1px solid ${D.borderL}`,paddingTop:12,minHeight:160,animation:"fadeIn .4s ease"}}>{fallbackContent[rotation]}</div>
          </div>
        ):oracle?(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
              <div style={{fontSize:11,color:D.t2}}>
                {oracle.away_abbr} @ {oracle.home_abbr}{oracle.venue_name?` · ${oracle.venue_name}`:""}{!isLive&&!isFinal?` · ${formatTime(oracle.scheduled_time)}`:""}
              </div>
              {isLive&&oracleGame&&(
                <div style={{display:"flex",alignItems:"center",gap:5}}>
                  <span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite",display:"inline-block"}}>●</span>
                  <span style={{fontSize:10,fontWeight:600,color:D.live}}>{oracleGame.inning_half==="top"?"Top":"Bot"} {oracleGame.inning}</span>
                </div>
              )}
              {isFinal&&(
                <span style={{fontSize:9,fontWeight:700,color:oracle.result==="WIN"?D.green:oracle.result==="LOSS"?D.red:D.amber,background:oracle.result==="WIN"?`${D.green}15`:oracle.result==="LOSS"?`${D.red}15`:`${D.amber}15`,border:`1px solid ${oracle.result==="WIN"?D.green:oracle.result==="LOSS"?D.red:D.amber}30`,borderRadius:20,padding:"2px 8px"}}>
                  FINAL
                </span>
              )}
            </div>
            {(isLive||isFinal)&&oracleGame&&(
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:D.s2,borderRadius:10,padding:"10px 14px",marginBottom:10,border:`1px solid ${isLive?`${D.live}30`:D.borderL}`}}>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:D.t3,letterSpacing:.5,marginBottom:2}}>{oracle.away_abbr}</div>
                  <div style={{fontSize:32,fontWeight:700,color:D.t1,fontFamily:MONO,lineHeight:1}}>{oracleGame.away_runs??0}</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:10,color:D.t3,marginBottom:2}}>{isLive?"IN PROGRESS":"FINAL"}</div>
                  <div style={{fontSize:11,color:D.t2}}>Total: <span style={{fontWeight:700,color:(oracleGame.away_runs+oracleGame.home_runs)>parseFloat(oracle.pick?.split(" ")[1]||0)?D.green:D.t2,fontFamily:MONO}}>{oracleGame.away_runs+oracleGame.home_runs}</span></div>
                  <div style={{fontSize:9,color:D.t3,marginTop:2}}>Need: {oracle.pick}</div>
                </div>
                <div style={{textAlign:"center"}}>
                  <div style={{fontSize:9,color:D.t3,letterSpacing:.5,marginBottom:2}}>{oracle.home_abbr}</div>
                  <div style={{fontSize:32,fontWeight:700,color:D.t1,fontFamily:MONO,lineHeight:1}}>{oracleGame.home_runs??0}</div>
                </div>
              </div>
            )}
            {isFinal&&(
              <div style={{display:"flex",alignItems:"center",gap:12,background:oracle.result==="WIN"?`${D.green}10`:oracle.result==="LOSS"?`${D.red}10`:`${D.amber}10`,border:`1px solid ${oracle.result==="WIN"?D.green:oracle.result==="LOSS"?D.red:D.amber}25`,borderRadius:12,padding:"12px 16px",marginBottom:10}}>
                <div style={{fontSize:32}}>{oracle.result==="WIN"?"✓":oracle.result==="LOSS"?"✗":"◌"}</div>
                <div>
                  <div style={{fontSize:16,fontWeight:700,color:oracle.result==="WIN"?D.green:oracle.result==="LOSS"?D.red:D.amber}}>{oracle.result==="WIN"?"WIN":oracle.result==="LOSS"?"LOSS":"PUSH"}</div>
                  <div style={{fontSize:11,color:D.t2}}>{oracle.pick} · Actual: {oracle.actual_total} runs · {parseFloat(oracle.pnl_units||0)>=0?"+":""}{parseFloat(oracle.pnl_units||0).toFixed(2)}u</div>
                </div>
              </div>
            )}
            <div style={{display:"flex",alignItems:"baseline",gap:12,flexWrap:"wrap",marginBottom:6}}>
              <div style={{fontSize:44,fontWeight:700,color:isFinal?(oracle.result==="WIN"?D.green:oracle.result==="LOSS"?D.red:D.amber):D.t1,lineHeight:1,letterSpacing:-1}}>{oracle.pick}</div>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:22,fontWeight:700,color:D.green,fontFamily:MONO}}>{oracle.odds>0?`+${oracle.odds}`:oracle.odds}</span>
                  <span style={{fontSize:11,color:D.t2}}>@ {bm?.label||oracle.book}</span>
                </div>
                <div style={{fontSize:11,fontWeight:600,color:D.oracle}}>Edge {oracle.edge_pct>0?"+":""}{parseFloat(oracle.edge_pct||0).toFixed(1)}% CLV</div>
              </div>
            </div>
            {oracle.rationale&&(
              <div style={{fontSize:12,color:D.t2,lineHeight:1.65,marginBottom:10,borderLeft:`2px solid ${D.oracle}`,paddingLeft:10}}>{oracle.rationale}</div>
            )}
            <div style={{height:1,background:`linear-gradient(90deg,${D.oracle},transparent)`,marginBottom:10,animation:"lineGrow .7s ease"}}/>
            {oracle.confidence_tier&&<ConfBadge tier={oracle.confidence_tier} onTip={onTip}/>}
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:12}}>
              <button onClick={()=>setShowCard(true)} style={{display:"flex",alignItems:"center",gap:6,padding:"10px 16px",borderRadius:10,border:"none",background:D.oracle,color:"#000",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:SANS}}>
                ↑ Share Pick
              </button>
              {bm&&(isAvailable&&isAvailable(oracle.book)?(
                <button onClick={()=>{const isAndroid=/android/i.test(navigator.userAgent);window.open(isAndroid&&bm.linkAndroid?bm.linkAndroid:bm.link,"_blank","noopener,noreferrer");}}
                  style={{display:"flex",alignItems:"center",gap:6,padding:"10px 16px",borderRadius:10,border:`1px solid ${D.border}`,color:D.t1,fontSize:13,fontWeight:600,background:"transparent",cursor:"pointer",fontFamily:SANS}}>
                  Bet {bm.short} ↗
                </button>
              ):(
                <div style={{padding:"10px 14px",borderRadius:10,border:`1px solid ${D.borderL}`,fontSize:11,color:D.t3}}>{bm.short} not in your state</div>
              ))}
            </div>
            {isAvailable&&(
              <div style={{marginTop:10}}>
                <div style={{fontSize:8,color:D.t3,letterSpacing:1,marginBottom:6}}>BET THIS PICK AT</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {BO.map(bk=>{
                    const b=BOOKS[bk]; if(!b) return null;
                    const avail=isAvailable(bk);
                    return(
                      <div key={bk} onClick={avail?()=>{const isAndroid=/android/i.test(navigator.userAgent);window.open(isAndroid&&b.linkAndroid?b.linkAndroid:b.link,"_blank","noopener,noreferrer");}:undefined}
                        style={{fontSize:10,fontWeight:700,color:avail?b.c:D.t4,border:`1px solid ${avail?`${b.c}30`:D.borderL}`,background:avail?`${b.c}10`:"transparent",borderRadius:7,padding:"4px 10px",cursor:avail?"pointer":"default",opacity:avail?1:.4}}>
                        {b.short}{avail?" ↗":""}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ):(
          <div style={{animation:"fadeIn .4s ease"}}>
            {/* ── NO ORACLE TODAY — show Sharp Pick instead if available ── */}
            {bestGame?(
              <div style={{marginBottom:14}}>
                {/* Header with live indicator */}
                <div style={{fontSize:9,fontWeight:700,color:D.blue,letterSpacing:1.8,marginBottom:10,
                  textAlign:"center",display:"flex",justifyContent:"center",alignItems:"center",gap:8}}>
                  <span>🔵 NO ORACLE · SHARP PICK OF THE DAY</span>
                  {bestIsLive&&(
                    <span style={{display:"flex",alignItems:"center",gap:4}}>
                      <span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite"}}>●</span>
                      <span style={{fontSize:9,fontWeight:600,color:D.live}}>
                        {bestGameLive?.inning_half==="top"?"Top":"Bot"} {bestGameLive?.inning}
                      </span>
                    </span>
                  )}
                  {bestIsFinal&&<span style={{fontSize:9,fontWeight:700,color:D.t3}}>FINAL</span>}
                </div>
                <div style={{background:`${D.blue}08`,
                  border:`1px solid ${bestIsLive?`${D.live}40`:D.blue+"25"}`,
                  borderRadius:16,padding:"14px 16px"}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:28,fontWeight:700,color:D.t1,letterSpacing:-.5}}>
                      {bestGame.away_abbr}@{bestGame.home_abbr}
                    </div>
                    <ConfBadge tier={bestGame.confidence_tier} onTip={onTip}/>
                  </div>

                  {/* Live / Final score block */}
                  {(bestIsLive||bestIsFinal)&&bestGameLive&&(
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                      background:D.s2,borderRadius:10,padding:"10px 14px",marginBottom:10,
                      border:`1px solid ${bestIsLive?`${D.live}30`:D.borderL}`}}>
                      <div style={{textAlign:"center"}}>
                        <div style={{fontSize:9,color:D.t3,letterSpacing:.5,marginBottom:2}}>{bestGame.away_abbr}</div>
                        <div style={{fontSize:32,fontWeight:700,color:D.t1,fontFamily:MONO,lineHeight:1}}>{bestGameLive.away_runs??0}</div>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <div style={{fontSize:10,color:D.t3,marginBottom:2}}>{bestIsLive?"IN PROGRESS":"FINAL"}</div>
                        <div style={{fontSize:11,color:D.t2}}>
                          Total: <span style={{fontWeight:700,fontFamily:MONO,
                            color:((bestGameLive.away_runs??0)+(bestGameLive.home_runs??0))>parseFloat(bestGame.market_line)?D.green:D.t2}}>
                            {(bestGameLive.away_runs??0)+(bestGameLive.home_runs??0)}
                          </span>
                        </div>
                        <div style={{fontSize:9,color:D.t3,marginTop:2}}>
                          Need: {parseFloat(bestGame.edge_pct)>0?'OVER':'UNDER'} {bestGame.market_line}
                        </div>
                      </div>
                      <div style={{textAlign:"center"}}>
                        <div style={{fontSize:9,color:D.t3,letterSpacing:.5,marginBottom:2}}>{bestGame.home_abbr}</div>
                        <div style={{fontSize:32,fontWeight:700,color:D.t1,fontFamily:MONO,lineHeight:1}}>{bestGameLive.home_runs??0}</div>
                      </div>
                    </div>
                  )}

                  <div style={{fontSize:18,fontWeight:700,color:D.t1,marginBottom:4}}>
                    {parseFloat(bestGame.edge_pct)>0?'OVER':'UNDER'} {bestGame.market_line}
                  </div>
                  {!bestIsLive&&!bestIsFinal&&(
                    <div style={{fontSize:11,color:D.t2,marginBottom:10}}>
                      Model projects {bestGame.projected_total} runs · {formatTime(bestGame.scheduled_time)}
                    </div>
                  )}
                  <div style={{display:"flex",gap:12,marginTop:bestIsLive||bestIsFinal?8:0}}>
                    <div style={{flex:1,background:D.s2,borderRadius:8,padding:"6px 10px",border:`1px solid ${D.borderL}`}}>
                      <div style={{fontSize:10,fontWeight:700,color:D.blue,fontFamily:MONO}}>
                        {parseFloat(bestGame.edge_pct)>0?"+":""}{parseFloat(bestGame.edge_pct).toFixed(1)}%
                      </div>
                      <div style={{fontSize:8,color:D.t3,marginTop:1}}>EDGE</div>
                    </div>
                    <div style={{flex:1,background:D.s2,borderRadius:8,padding:"6px 10px",border:`1px solid ${D.borderL}`}}>
                      <div style={{fontSize:10,fontWeight:700,color:D.t1,fontFamily:MONO}}>
                        {bestGame.away_pitcher?.split(' ').pop()||'TBD'} vs {bestGame.home_pitcher?.split(' ').pop()||'TBD'}
                      </div>
                      <div style={{fontSize:8,color:D.t3,marginTop:1}}>PITCHERS</div>
                    </div>
                  </div>
                  <div style={{marginTop:10,fontSize:9,color:D.t3,textAlign:"center",letterSpacing:.5}}>
                    Not an Oracle pick — no confirmation signal fired today
                  </div>
                </div>
              </div>
            ):(
              <div style={{textAlign:"center",padding:"10px 4px 18px"}}>
                <div style={{fontSize:28,marginBottom:10}}>🎯</div>
                <div style={{fontSize:15,fontWeight:700,color:D.t1,marginBottom:8}}>No Oracle Today</div>
                <div style={{fontSize:12,color:D.t2,lineHeight:1.8,marginBottom:14,maxWidth:320,margin:"0 auto 14px"}}>
                  The model reviewed today's full slate and found no game where edge, confidence, and chaos aligned to our standard. The Oracle only fires when the signal is genuine — that's what makes it worth following.
                </div>
                <div style={{fontSize:8,fontWeight:700,color:D.t3,letterSpacing:1.8}}>DISCIPLINE IS THE EDGE</div>
              </div>
            )}
            {/* Track record — always visible on no-action days */}
            {trackRecord&&trackRecord.length>0&&(()=>{
              const allSettled=trackRecord.filter(r=>r.result&&r.result!=="PENDING");
              const wins=allSettled.filter(r=>r.result==="WIN").length;
              const losses=allSettled.filter(r=>r.result==="LOSS").length;
              const totalPnl=allSettled.reduce((s,r)=>s+parseFloat(r.pnl_units||0),0);
              const settled=allSettled.slice(0,5);
              if(!settled.length) return null;
              return(
                <div style={{borderTop:`1px solid ${D.borderL}`,paddingTop:12,marginTop:4}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                    <div style={{fontSize:7,fontWeight:700,color:D.t3,letterSpacing:1.5}}>ORACLE TRACK RECORD</div>
                    <div style={{fontSize:10,fontWeight:700,color:totalPnl>=0?D.green:D.red,fontFamily:MONO}}>
                      {wins}-{losses} · {totalPnl>=0?"+":""}{totalPnl.toFixed(1)}u
                    </div>
                  </div>
                  {settled.map((r,i)=>{
                    const col=r.result==="WIN"?D.green:r.result==="PUSH"?D.amber:D.red;
                    const pnl=parseFloat(r.pnl_units||0);
                    return(
                      <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 0",borderBottom:i<settled.length-1?`1px solid ${D.t4}`:""}}>
                        <div style={{fontSize:8,color:D.t3,width:36,flexShrink:0,fontFamily:MONO}}>
                          {new Date(r.date+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                        </div>
                        <div style={{flex:1,fontSize:10,fontWeight:600,color:D.t1}}>{r.pick}</div>
                        <div style={{fontSize:7,fontWeight:700,color:col,background:`${col}15`,border:`1px solid ${col}30`,borderRadius:20,padding:"2px 7px",letterSpacing:.5}}>
                          {r.result}
                        </div>
                        <div style={{fontSize:9,fontWeight:700,color:col,fontFamily:MONO,width:38,textAlign:"right"}}>
                          {pnl>=0?"+":""}{pnl.toFixed(2)}u
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            {/* Slate summary still visible below */}
            <div style={{borderTop:`1px solid ${D.borderL}`,paddingTop:12,marginTop:12}}>
              {fallbackContent[1]}
            </div>
          </div>
        )}
        {showCard&&<OracleShareCard oracle={oracle} onClose={()=>setShowCard(false)}/>}
      </div>
    </div>
  );
};

const HowItWorks = ({onTip})=>{
  const [open,setOpen]=useState(false);
  const items=[
    {icon:"📊",label:"Sharp Edge",tip:"edge",desc:"The % gap between our model's projection and the closing line. Aka Closing Line Value (CLV) — the gold standard for measuring bet quality."},
    {icon:"🌀",label:"Chaos Score",tip:"chaos",desc:"0-100 volatility rating. CALM = neutral, STEADY = minor factors, ACTIVE = real volatility, CHAOS = high unpredictability. High chaos tends to favor overs."},
    {icon:"⚡",label:"The Oracle",tip:"oracle",desc:"Our lock of the day. Only fires when HIGH or ELITE confidence aligns with a meaningful edge. Quality over quantity."},
    {icon:"🎯",label:"Confidence",tip:"confidence",desc:"ELITE = all 8 factors aligned. HIGH = strong signal. MEDIUM = worth watching. LOW = lean only. PASS = no play."},
  ];
  return(
    <div style={{marginBottom:14}}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:"100%",display:"flex",justifyContent:"space-between",alignItems:"center",background:D.s1,border:`1px solid ${D.borderL}`,borderRadius:open?"12px 12px 0 0":12,padding:"11px 14px",cursor:"pointer",fontFamily:SANS}}>
        <span style={{fontSize:11,fontWeight:600,color:D.t2,letterSpacing:.5}}>📖 How SharpInning Works</span>
        <span style={{fontSize:11,color:D.t3}}>{open?"▲":"▼"}</span>
      </button>
      {open&&(
        <div style={{background:D.s1,border:`1px solid ${D.borderL}`,borderTop:"none",borderRadius:"0 0 12px 12px",padding:"4px 0 8px"}}>
          {items.map(({icon,label,tip,desc})=>(
            <div key={label} onClick={()=>onTip&&onTip(TIPS[tip].title,TIPS[tip].body)}
              style={{display:"flex",gap:12,padding:"10px 14px",cursor:"pointer",borderBottom:`1px solid ${D.borderL}`}}>
              <span style={{fontSize:18,flexShrink:0}}>{icon}</span>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
                  <span style={{fontSize:12,fontWeight:600,color:D.t1}}>{label}</span>
                  <span style={{fontSize:9,color:D.blue}}>Tap to learn more →</span>
                </div>
                <div style={{fontSize:11,color:D.t2,lineHeight:1.55}}>{desc}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SharpSummary = ({games,lastUpdated,onGameClick})=>{
  const sharp=games?.filter(g=>parseFloat(g.edge_pct||0)>4&&g.status!=="final").sort((a,b)=>parseFloat(b.edge_pct)-parseFloat(a.edge_pct)).slice(0,4);
  return(
    <div style={{marginBottom:14}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.5}}>SHARP SUMMARY</div>
        {lastUpdated&&<div style={{fontSize:9,color:D.t3}}>Updated {new Date(lastUpdated).toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"})}</div>}
      </div>
      {!sharp?.length?(
        <div style={{background:D.s1,borderRadius:12,padding:"12px 14px",border:`1px solid ${D.borderL}`,fontSize:11,color:D.t3,textAlign:"center"}}>Sharp angles surface after model runs at 10am ET</div>
      ):(
        <div style={{display:"grid",gridTemplateColumns:"repeat(2,1fr)",gap:8}}>
          {sharp.map(g=>(
            <div key={g.game_id} onClick={()=>onGameClick&&onGameClick(g.game_id)}
              style={{background:D.s1,borderRadius:12,padding:"10px 12px",border:`1px solid ${D.borderL}`,cursor:"pointer",transition:"border-color .15s, background .15s"}}
              onMouseEnter={e=>{e.currentTarget.style.borderColor=D.blue;e.currentTarget.style.background=D.s2;}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(84,84,88,.35)";e.currentTarget.style.background=D.s1;}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:5}}>
                <span style={{fontSize:13,fontWeight:700,color:D.t1}}>{g.away_abbr}@{g.home_abbr}</span>
                <ConfBadge tier={g.confidence_tier}/>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                <span style={{fontSize:10,fontWeight:600,color:D.green,fontFamily:MONO}}>OVER {g.market_line||"—"}</span>
                <span style={{fontSize:11,fontWeight:700,color:D.blue,fontFamily:MONO}}>+{parseFloat(g.edge_pct||0).toFixed(1)}%</span>
              </div>
              <div style={{fontSize:9,color:D.t3,marginTop:3}}>{formatTime(g.scheduled_time)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WeatherAlert = ({games})=>{
  const highWind=games?.filter(g=>parseFloat(g.wind_mph||0)>=15&&g.wind_direction==="out"&&g.status!=="final");
  const highRain=games?.filter(g=>parseInt(g.rain_probability||0)>=60&&g.status!=="final");
  if(!highWind?.length&&!highRain?.length) return null;
  const alerts=[...highWind.map(g=>`⚡ ${g.away_abbr}@${g.home_abbr}: ${Math.round(g.wind_mph)}mph out`),...highRain.map(g=>`🌧 ${g.away_abbr}@${g.home_abbr}: Rain ${g.rain_probability}%`)];
  return(
    <div style={{background:"rgba(255,159,10,.08)",border:"1px solid rgba(255,159,10,.25)",borderRadius:10,padding:"8px 12px",marginBottom:10,fontSize:11,color:D.amber,display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
      <span style={{fontWeight:700,flexShrink:0,letterSpacing:.5}}>WEATHER</span>
      {alerts.map((a,i)=><span key={i} style={{color:D.t2}}>{a}</span>)}
    </div>
  );
};

const ScheduleStrip = ({selectedDate,onSelectDate,gameCounts,todayGames})=>{
  const days=[];
  for(let i=0;i<7;i++){
    const d=new Date(); d.setDate(d.getDate()+i);
    const dateStr=d.toLocaleDateString("en-CA",{timeZone:"America/New_York"});
    days.push({date:dateStr,short:i===0?"Today":i===1?"Tmrw":d.toLocaleDateString("en-US",{month:"numeric",day:"numeric"}),count:i===0?todayGames:(gameCounts[dateStr]||0)});
  }
  return(
    <div>
      <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:8}}>SCHEDULE</div>
      <div style={{display:"flex",gap:6,overflowX:"auto",paddingBottom:4,scrollbarWidth:"none"}}>
        {days.map(d=>{
          const active=d.date===selectedDate;
          return(
            <button key={d.date} onClick={()=>onSelectDate(d.date)}
              style={{flexShrink:0,padding:"7px 12px",borderRadius:20,border:`1px solid ${active?D.blue:D.borderL}`,background:active?`${D.blue}20`:"transparent",cursor:"pointer",fontSize:11,fontWeight:active?600:400,color:active?D.blue:D.t2,fontFamily:SANS,transition:"all .15s",whiteSpace:"nowrap",display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
              <span>{d.short}</span>
              {d.count>0&&<span style={{fontSize:8,color:active?D.blue:D.t3}}>{d.count}G</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const FilterBar = ({filter,setFilter,sort,setSort})=>(
  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
    {/* Row 1: filter pills — full width */}
    <div style={{display:"flex",gap:4,overflowX:"auto",scrollbarWidth:"none",WebkitOverflowScrolling:"touch"}}>
      {["ALL","ELITE","HIGH","MEDIUM"].map(f=>{
        const cs=confStyle(f==="ALL"?null:f); const active=filter===f;
        return(
          <button key={f} onClick={()=>setFilter(f)}
            style={{flexShrink:0,padding:"5px 12px",borderRadius:20,border:`1px solid ${active?(f==="ALL"?D.border:cs.b):D.borderL}`,background:active?(f==="ALL"?"rgba(255,255,255,.06)":cs.bg):"transparent",cursor:"pointer",fontSize:10,fontWeight:active?700:400,color:active?(f==="ALL"?D.t1:cs.c):D.t3,fontFamily:SANS,transition:"all .12s"}}>
            {f}
          </button>
        );
      })}
      <button onClick={()=>setFilter("PLAY")}
        style={{flexShrink:0,padding:"5px 12px",borderRadius:20,
          border:`1px solid ${filter==="PLAY"?D.violet+"80":D.borderL}`,
          background:filter==="PLAY"?`${D.violet}20`:"transparent",
          cursor:"pointer",fontSize:10,fontWeight:filter==="PLAY"?700:400,
          color:filter==="PLAY"?D.violet:D.t3,fontFamily:SANS,transition:"all .12s"}}>
        THE PLAY
      </button>
    </div>
    {/* Row 2: sort dropdown — right aligned */}
    <div style={{display:"flex",justifyContent:"flex-end"}}>
      <select value={sort} onChange={e=>setSort(e.target.value)}
        style={{background:D.s2,border:`1px solid ${D.borderL}`,borderRadius:8,color:D.t2,fontSize:10,padding:"5px 8px",cursor:"pointer",fontFamily:SANS}}>
        <option value="time">By Time</option>
        <option value="edge">By Edge</option>
        <option value="chaos">By Chaos</option>
        <option value="play">By The Play</option>
      </select>
    </div>
  </div>
);

// ── LINE MOVE PANEL ───────────────────────────────────────────
const LineMovePanel = ({ gameId }) => {
  const moves = useLineMoves(gameId);
  if (!moves.length) return null;

  const lines = moves.map(r => parseFloat(r.current_line || 0)).filter(Boolean);
  const uniqueLines = [...new Set(lines)];
  const hasMovement = moves.some(r => parseFloat(r.movement || 0) !== 0);
  const booksDisagree = uniqueLines.length > 1;
  if (!hasMovement && !booksDisagree) return null;

  const sorted = [...moves].sort((a, b) => parseFloat(b.movement || 0) - parseFloat(a.movement || 0));

  const sortedLines = [...lines].sort((a, b) => a - b);
  const mid = Math.floor(sortedLines.length / 2);
  const median = sortedLines.length % 2 !== 0
    ? sortedLines[mid]
    : parseFloat(((sortedLines[mid - 1] + sortedLines[mid]) / 2).toFixed(1));

  return (
    <div style={{ background: D.s2, borderRadius: 10, border: `1px solid ${D.borderL}`, padding: '10px 12px', marginTop: 10 }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: D.t3, letterSpacing: 1.5, marginBottom: 8 }}>
        LINE MOVEMENT BY BOOK
      </div>
      {sorted.map((row, i) => {
        const bm = BOOKS[row.book];
        if (!bm) return null;
        const moved = parseFloat(row.movement || 0);
        const open  = parseFloat(row.opening_line || 0);
        const cur   = parseFloat(row.current_line  || 0);
        const moveCol = moved < 0 ? D.red : moved > 0 ? D.green : D.t3;
        const isOutlier = Math.abs(cur - median) > 0.5;
        return (
          <div key={row.book} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 0',
            borderBottom: i < sorted.length - 1 ? `1px solid ${D.borderL}` : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, width: 80, flexShrink: 0 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: bm.c, display: 'inline-block', flexShrink: 0 }} />
              <span style={{ fontSize: 11, fontWeight: 700, color: bm.c, fontFamily: SANS }}>{bm.short}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, justifyContent: 'center' }}>
              <span style={{ fontSize: 12, color: D.t3, fontFamily: MONO }}>{open.toFixed(1)}</span>
              <span style={{ fontSize: 10, color: D.t3 }}>→</span>
              <span style={{ fontSize: 14, fontWeight: 700, fontFamily: MONO, color: moved !== 0 ? moveCol : D.t2 }}>
                {cur.toFixed(1)}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, justifyContent: 'flex-end', minWidth: 80 }}>
              {moved !== 0 ? (
                <span style={{
                  fontSize: 10, fontWeight: 700, color: moveCol,
                  background: `${moveCol}15`, border: `1px solid ${moveCol}30`,
                  borderRadius: 20, padding: '2px 8px', fontFamily: MONO,
                }}>
                  {moved > 0 ? '+' : ''}{moved.toFixed(1)}
                </span>
              ) : (
                <span style={{ fontSize: 9, color: D.t3 }}>no move</span>
              )}
              {isOutlier && (
                <span style={{
                  fontSize: 7, fontWeight: 700, color: D.amber,
                  background: `${D.amber}15`, border: `1px solid ${D.amber}30`,
                  borderRadius: 20, padding: '2px 5px', letterSpacing: 0.5,
                }}>
                  ODD
                </span>
              )}
            </div>
          </div>
        );
      })}
      {booksDisagree && (
        <div style={{
          marginTop: 8, paddingTop: 7, borderTop: `1px solid ${D.borderL}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 9, color: D.t3 }}>
            Books split: {uniqueLines.map(l => l.toFixed(1)).join(' / ')}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: D.blue, fontFamily: MONO }}>
            Median: {median.toFixed(1)}
          </span>
        </div>
      )}
    </div>
  );
};

// ── GAME CARD ──────────────────────────────────────────────────
const GameCard = ({row,odds,pollData,onVote,aiText,onAnalyze,analyzing,marketIntelData,onMarketIntel,marketIntelLoading,postGameSummary,onPostGameSummary,summaryLoading,index,isAvailable,onTip,isHighlighted,cardRef})=>{
  const [section,setSection]=useState("why");
  const [open,setOpen]=useState(false);
  const tv=(pollData.over||0)+(pollData.under||0);
  const lineMove=[parseFloat(row.snap_line_open||row.market_line||8.5),parseFloat(row.market_line||8.5)];
  const isFinal=row.status==="final";
  const isLive=row.status==="live";
  const countdown=useCountdown(isLive||isFinal?null:row.scheduled_time);

  useEffect(()=>{
    if(isHighlighted){setOpen(true);cardRef?.current?.scrollIntoView({behavior:"smooth",block:"start"});}
  },[isHighlighted,cardRef]);

  const notes=[
    row.snap_line_movement&&Math.abs(row.snap_line_movement)>=.5?`Line moved ${Math.abs(row.snap_line_movement)} — ${row.snap_line_movement>0?"over":"under"} steam detected`:null,
    // CLV agreement indicator — does market movement confirm or contradict model lean?
    (()=>{
      const ep = parseFloat(row.edge_pct||0);
      const mv = parseFloat(row.snap_line_movement||0);
      if(!ep || !mv || Math.abs(mv) < 0.5) return null;
      const modelLeanOver = ep > 0;
      const marketMovedOver = mv > 0;
      const agrees = modelLeanOver === marketMovedOver;
      if(agrees) return `Market agrees — line moved ${mv>0?"+":""}${mv} confirming ${modelLeanOver?"over":"under"} lean. Sharp money aligns with model.`;
      return `Market disagrees — line moved ${mv>0?"+":""}${mv} against model lean. Sharp money may have better information. Treat with caution.`;
    })(),
    row.snap_book_spread>=.5?`Books split by ${row.snap_book_spread} — market disagreement signals sharp action`:null,
    row.projected_total&&row.market_line?`Model projects ${row.projected_total} runs vs market line ${row.market_line}`:null,
    row.away_pitcher&&row.home_pitcher?`${row.away_pitcher} vs ${row.home_pitcher} on the mound`:null,
    // STEP 6: pass edge_pct so wNarrative can suppress conflicting lean language
    wNarrative(row, row.edge_pct)||null,
  ].filter(Boolean);

  const edgeTipTitle = row.edge_pct&&row.market_line
    ? `Sharp Edge +${parseFloat(row.edge_pct).toFixed(1)}% — ${row.away_abbr}@${row.home_abbr}`
    : TIPS.edge.title;
  const edgeTipBody = row.edge_pct&&row.market_line&&row.projected_total
    ? `Our model projects ${row.projected_total} total runs for ${row.away_abbr}@${row.home_abbr}. The market line is set at ${row.market_line}. That +${parseFloat(row.edge_pct).toFixed(1)}% gap is the Sharp Edge — we see ${parseFloat(row.edge_pct).toFixed(1)}% more value here than the books are currently offering.\n\nThis metric is known as Closing Line Value (CLV) in betting circles. Sharp bettors follow CLV because beating the closing line is the most reliable indicator of long-term profitability.\n\nAny edge above 4% is worth watching. Above 15%, the model has strong conviction. The Oracle only fires when edge and confidence align.`
    : TIPS.edge.body;

  return(
    <div ref={cardRef}
      className={`${isFinal?"":"card-enter"}${isHighlighted?" game-highlight":""}`}
      style={{animationDelay:`${Math.min(index,5)*50}ms`,background:D.s1,borderRadius:16,overflow:"hidden",marginBottom:10,border:`1px solid ${isHighlighted?D.blue:isLive?"rgba(48,209,88,.2)":D.border}`,opacity:isFinal?.72:1,transition:"border-color .3s"}}>
      <div style={{padding:"14px 16px 10px",cursor:"pointer"}} onClick={()=>setOpen(o=>!o)}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
          <div>
            <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:2}}>
              {isLive&&<span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite",display:"inline-block",flexShrink:0}}>●</span>}
              <span style={{fontSize:24,fontWeight:700,color:D.t1,letterSpacing:-.5,lineHeight:1}}>
                {row.away_abbr}<span style={{color:D.t2,fontWeight:300,margin:"0 6px",fontSize:20}}>@</span>{row.home_abbr}
              </span>
              {row.game_number>1&&(
                <span style={{fontSize:8,fontWeight:700,color:D.amber,background:`${D.amber}18`,border:`1px solid ${D.amber}35`,borderRadius:20,padding:"2px 7px",letterSpacing:.8,flexShrink:0}}>GM {row.game_number}</span>
              )}

            </div>
            <div style={{fontSize:10,color:D.t2}}>
              {row.away_pitcher&&row.home_pitcher?(
                <span>
                  {row.away_pitcher.split(" ").slice(-1)[0]}
                  {row.away_era!=null&&<span style={{fontSize:10,color:D.t2,fontFamily:MONO,fontWeight:600}}> {parseFloat(row.away_era).toFixed(2)}</span>}
                  <span style={{color:D.t3,margin:"0 4px"}}>vs</span>
                  {row.home_pitcher.split(" ").slice(-1)[0]}
                  {row.home_era!=null&&<span style={{fontSize:10,color:D.t2,fontFamily:MONO,fontWeight:600}}> {parseFloat(row.home_era).toFixed(2)}</span>}
                </span>
              ):row.venue_name||"TBD"}
            </div>
            {/* Team trend — shown as compact inline pair below pitcher line */}
            {(()=>{
              const at=teamTrend(row.away_rs_30,row.away_rs_7,row.away_ra_30,row.away_ra_7);
              const ht=teamTrend(row.home_rs_30,row.home_rs_7,row.home_ra_30,row.home_ra_7);
              if((!at||at==='NEUTRAL')&&(!ht||ht==='NEUTRAL')) return null;
              return(
                <div style={{display:"flex",gap:4,marginTop:3}}>
                  {at&&at!=='NEUTRAL'&&(
                    <span style={{fontSize:7,fontWeight:700,
                      color:at==='HOT'?D.green:D.cyan,
                      background:at==='HOT'?`${D.green}18`:`${D.cyan}18`,
                      border:`1px solid ${at==='HOT'?D.green:D.cyan}35`,
                      borderRadius:20,padding:"1px 6px",letterSpacing:.8}}>
                      {row.away_abbr} {at==='HOT'?'🔥':'❄️'}
                    </span>
                  )}
                  {ht&&ht!=='NEUTRAL'&&(
                    <span style={{fontSize:7,fontWeight:700,
                      color:ht==='HOT'?D.green:D.cyan,
                      background:ht==='HOT'?`${D.green}18`:`${D.cyan}18`,
                      border:`1px solid ${ht==='HOT'?D.green:D.cyan}35`,
                      borderRadius:20,padding:"1px 6px",letterSpacing:.8}}>
                      {row.home_abbr} {ht==='HOT'?'🔥':'❄️'}
                    </span>
                  )}
                </div>
              );
            })()}
          </div>
          <div style={{textAlign:"right",flexShrink:0}}>
            {isFinal?(
              <div>
                <div style={{fontSize:13,fontWeight:700,color:D.t2,fontFamily:MONO}}>FINAL</div>
                <div style={{fontSize:11,color:D.t2,fontFamily:MONO,marginTop:1}}>{row.away_abbr} {row.away_runs??""} — {row.home_runs??""} {row.home_abbr}</div>
              </div>
            ):isLive?(
              <div>
                <div style={{fontSize:13,fontWeight:700,color:D.live,fontFamily:MONO}}>{row.away_runs??0} — {row.home_runs??0}</div>
                <div style={{fontSize:10,color:D.live,marginTop:1}}>{row.inning_half} {row.inning}</div>
              </div>
            ):(
              <div>
                {row.game_number>1&&<div style={{fontSize:8,fontWeight:700,color:D.amber,letterSpacing:.8,marginBottom:2}}>GAME {row.game_number}</div>}
                <div style={{fontSize:14,fontWeight:600,color:D.blue,fontFamily:MONO}}>{formatTime(row.scheduled_time)}</div>
                {countdown&&<div style={{fontSize:10,color:D.t2,marginTop:2}}>in {countdown}</div>}
              </div>
            )}
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
          <div style={{flex:1,fontSize:10,color:D.t2}}>{row.temp_f?`${row.temp_f}° · ${Math.round(row.wind_mph||0)}mph ${row.wind_direction||""}`:row.venue_name||""}</div>
          {row.market_line&&(
            <div style={{display:"flex",alignItems:"center",gap:4}}>
              <span style={{fontSize:10,color:D.t2,fontFamily:MONO}}>O/U {row.market_line}</span>
              <Spark data={lineMove}/>
            </div>
          )}
          {row.chaos_total!=null&&<ChaosGauge score={row.chaos_total} size={64} onTip={onTip}/>}
          {row.confidence_tier&&<ConfBadge tier={row.confidence_tier} onTip={onTip}/>}
        </div>
        {/* STEP 8: THE PLAY banner — suppressed when ERA missing, LOW = WEAK SIGNAL */}
        {(()=>{
          const ep = parseFloat(row.edge_pct||0);
          const isOver  = ep > 4  && row.confidence_tier !== "PASS";
          const isUnder = ep < -10 || (ep < -4 && row.confidence_tier !== "PASS");
          if((!isOver && !isUnder) || !row.market_line) return null;

          // ERA missing — show SIGNAL PENDING instead of THE PLAY
          const hasEra = row.away_era != null || row.home_era != null;
          if(!hasEra) return(
            <div style={{marginTop:8,display:"flex",alignItems:"center",justifyContent:"space-between",
              background:"rgba(255,255,255,.04)",border:`1px solid ${D.borderL}`,
              borderRadius:12,padding:"8px 12px",opacity:0.6}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:8,fontWeight:700,color:D.t3,letterSpacing:1.2}}>SIGNAL PENDING</span>
                <span style={{fontSize:13,fontWeight:600,color:D.t3}}>{isOver?"OVER":"UNDER"} {row.market_line}</span>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:9,color:D.t3}}>ERA not yet confirmed</div>
                <div style={{fontSize:9,color:D.t4}}>Edge {ep>0?"+":""}{ep.toFixed(1)}% unverified</div>
              </div>
            </div>
          );

          const isWeak    = row.confidence_tier === "LOW";
          const direction = isOver ? "OVER" : "UNDER";
          const edgeStr   = `${ep > 0 ? "+" : ""}${ep.toFixed(1)}%`;

          // ── WIN/LOSS RESULT when game is final ──────────────
          if(isFinal && !isWeak) {
            const totalRuns = (parseInt(row.away_runs||0) + parseInt(row.home_runs||0));
            const won = isOver
              ? totalRuns > parseFloat(row.market_line)
              : totalRuns < parseFloat(row.market_line);
            const pushed = totalRuns === parseFloat(row.market_line);
            const resultColor = pushed ? D.amber : won ? D.green : D.red;
            const resultLabel = pushed ? "PUSH" : won ? "WON" : "LOST";
            const resultIcon  = pushed ? "—" : won ? "✓" : "✗";
            return(
              <div style={{marginTop:8,borderRadius:12,overflow:"hidden",border:`1px solid ${resultColor}30`}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",
                  background:`${resultColor}10`,padding:"8px 12px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:8,fontWeight:700,color:resultColor,letterSpacing:1.2}}>THE PLAY</span>
                    <span style={{fontSize:17,fontWeight:700,color:D.t1}}>{direction} {row.market_line}</span>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:13,fontWeight:700,color:resultColor}}>{resultIcon} {resultLabel} · {totalRuns} runs</div>
                    <div style={{fontSize:9,color:D.t3}}>Edge was {edgeStr}</div>
                  </div>
                </div>
                {/* Post-game summary */}
                {postGameSummary?(
                  <div style={{padding:"8px 12px",background:`${resultColor}08`,borderTop:`1px solid ${resultColor}20`}}>
                    <div style={{fontSize:11,color:D.t2,lineHeight:1.6}}>{postGameSummary}</div>
                  </div>
                ):(
                  <div style={{padding:"6px 12px",background:`${resultColor}08`,borderTop:`1px solid ${resultColor}20`,
                    display:"flex",justifyContent:"center"}}>
                    <button onClick={e=>{e.stopPropagation();onPostGameSummary&&onPostGameSummary(row);}}
                      style={{background:"transparent",border:"none",cursor:"pointer",
                        fontSize:10,color:resultColor,fontWeight:600,opacity:summaryLoading?0.5:1}}>
                      {summaryLoading?"Analyzing...":"💬 Why did this "+ (resultLabel==="WON"?"win":resultLabel==="LOST"?"lose":"push") + "?"}
                    </button>
                  </div>
                )}
              </div>
            );
          }

          const playColor = isWeak ? D.t3 : (isOver ? D.blue : D.violet);
          return(
            <div onClick={e=>{e.stopPropagation();onTip&&onTip(edgeTipTitle,edgeTipBody);}}
              style={{marginTop:8,display:"flex",alignItems:"center",justifyContent:"space-between",
                background:`${playColor}${isWeak?"08":"10"}`,
                border:`1px solid ${playColor}${isWeak?"20":"30"}`,
                borderRadius:12,padding:"8px 12px",cursor:"pointer",opacity:isWeak?0.75:1}}>
              <div style={{display:"flex",alignItems:"center",gap:8}}>
                <span style={{fontSize:8,fontWeight:700,color:playColor,letterSpacing:1.2}}>
                  {isWeak?"WEAK SIGNAL":row.away_starter_confirmed&&row.home_starter_confirmed?"THE PLAY":"THE PLAY · PROBABLE"}
                </span>
                <span style={{fontSize:isWeak?14:17,fontWeight:700,color:isWeak?D.t2:D.t1}}>{direction} {row.market_line}</span>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:11,fontWeight:700,color:playColor}}>Edge {edgeStr}</div>
                <div style={{fontSize:11,color:D.t2,fontWeight:500}}>Model projects {row.projected_total} runs ⓘ</div>
              </div>
            </div>
          );
        })()}
      </div>

      {open&&!isFinal&&(
        <div style={{animation:"fadeIn .18s ease"}}>
          <div style={{display:"flex",borderTop:`1px solid ${D.borderL}`,borderBottom:`1px solid ${D.borderL}`}}>
            {[{k:"why",l:"The Sharp Take"},{k:"ml",l:"Moneyline"},{k:"total",l:"Total"}].map(t=>(
              <button key={t.k} onClick={()=>setSection(t.k)}
                style={{flex:1,padding:"9px 4px",background:"none",border:"none",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:SANS,color:section===t.k?D.blue:D.t3,borderBottom:`2px solid ${section===t.k?D.blue:"transparent"}`,letterSpacing:.2}}>
                {t.l}
              </button>
            ))}
          </div>
          <div style={{padding:"12px 14px"}}>
            {section==="why"&&(
              <div>
                {/* ── TEAM TREND CARDS ─────────────────────────── */}
                {(row.away_rs_30&&row.away_rs_7&&row.home_rs_30&&row.home_rs_7)&&(
                  <div style={{display:"flex",gap:8,marginBottom:12}}>
                    {[
                      {abbr:row.away_abbr,rs30:row.away_rs_30,rs7:row.away_rs_7,ra30:row.away_ra_30,ra7:row.away_ra_7},
                      {abbr:row.home_abbr,rs30:row.home_rs_30,rs7:row.home_rs_7,ra30:row.home_ra_30,ra7:row.home_ra_7},
                    ].map(team=>{
                      const trend=teamTrend(team.rs30,team.rs7,team.ra30,team.ra7);
                      const offDelta=(parseFloat(team.rs7)-parseFloat(team.rs30)).toFixed(1);
                      const defDelta=(parseFloat(team.ra30)-parseFloat(team.ra7)).toFixed(1);
                      const tColor=trend==='HOT'?D.green:trend==='COLD'?D.cyan:D.t3;
                      return(
                        <div key={team.abbr} style={{flex:1,background:D.s2,borderRadius:8,
                          padding:"8px 10px",border:`1px solid ${trend==='NEUTRAL'?D.borderL:`${tColor}25`}`}}>
                          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                            <span style={{fontSize:10,fontWeight:700,color:D.t1}}>{team.abbr}</span>
                            <span style={{fontSize:8,fontWeight:700,color:tColor,
                              background:`${tColor}15`,borderRadius:10,padding:"1px 6px"}}>
                              {trend==='HOT'?'🔥 HOT':trend==='COLD'?'❄️ COLD':'→ NEUTRAL'}
                            </span>
                          </div>
                          <div style={{fontSize:9,color:D.t3,lineHeight:1.8}}>
                            <div>OFF <span style={{color:parseFloat(offDelta)>0?D.green:parseFloat(offDelta)<0?D.red:D.t2,fontFamily:MONO,fontWeight:600}}>{parseFloat(offDelta)>0?'+':''}{offDelta}</span> RS/G vs 30d</div>
                            <div>DEF <span style={{color:parseFloat(defDelta)>0?D.green:parseFloat(defDelta)<0?D.red:D.t2,fontFamily:MONO,fontWeight:600}}>{parseFloat(defDelta)>0?'+':''}{defDelta}</span> RA/G vs 30d</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {notes.length?(
                  notes.map((n,i)=>(
                    <div key={i} style={{display:"flex",gap:8,alignItems:"flex-start",padding:"8px 0",borderBottom:i<notes.length-1?`1px solid ${D.borderL}`:"none"}}>
                      <span style={{color:D.blue,fontSize:10,marginTop:2,flexShrink:0}}>→</span>
                      <span style={{fontSize:14,color:D.t2,lineHeight:1.65}}>{n}</span>
                    </div>
                  ))
                ):(
                  <div style={{fontSize:14,color:D.t2,padding:"8px 0"}}>No sharp angle on this game yet.</div>
                )}
                <LineMovePanel gameId={row.game_id}/>
                <div style={{marginTop:10}}>
                  <div style={{display:"flex",gap:8,marginBottom:6}}>
                    {!aiText?(
                      <button onClick={()=>onAnalyze(row)}
                        style={{flex:1,padding:"9px",borderRadius:10,border:`1px solid ${D.border}`,background:"transparent",color:D.t2,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:SANS}}>
                        🧠 AI Read
                      </button>
                    ):null}
                    {(Math.abs(parseFloat(row.snap_line_movement||0))>=0.5||parseFloat(row.snap_book_spread||0)>=0.5)&&!marketIntelData&&(
                      <button onClick={()=>onMarketIntel(row)}
                        style={{flex:1,padding:"9px",borderRadius:10,border:`1px solid ${D.amber}50`,background:"transparent",color:D.amber,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:SANS}}>
                        {marketIntelLoading?"Analyzing...":"📡 Market Intel"}
                      </button>
                    )}
                  </div>
                  {marketIntelData&&(
                    <div style={{background:D.s2,borderRadius:10,border:`1px solid ${D.amber}40`,padding:"10px 12px",marginBottom:8}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                        <div style={{fontSize:8,fontWeight:700,color:D.amber,letterSpacing:1.2}}>MARKET INTEL</div>
                        <div style={{fontSize:8,color:D.t3}}>Score: {marketIntelData.score}/100 · {marketIntelData.confidence}</div>
                      </div>
                      {marketIntelData.stale_line&&(
                        <div style={{background:`${D.amber}15`,border:`1px solid ${D.amber}40`,borderRadius:6,padding:"5px 8px",marginBottom:6,fontSize:11,color:D.amber,fontWeight:600}}>
                          ⚡ {marketIntelData.stale_line}
                        </div>
                      )}
                      <div style={{fontSize:12,color:D.t2,lineHeight:1.6,whiteSpace:"pre-wrap"}}>{marketIntelData.analysis}</div>
                    </div>
                  )}
                  {!aiText?(
                    null
                  ):(
                    <div style={{background:D.s2,borderRadius:10,border:`1px solid ${D.borderL}`,padding:"10px 12px"}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                        <div style={{fontSize:8,fontWeight:700,color:D.violet,letterSpacing:1.2}}>AI READ</div>
                        <div style={{fontSize:8,color:D.t3}}>Independent analysis · may differ from model</div>
                      </div>
                      {analyzing===row.game_id
                        ?<div style={{fontSize:14,color:D.t2}}>Analyzing…</div>
                        :<div style={{fontSize:14,color:D.t2,lineHeight:1.75}}>{aiText}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}
            {(section==="ml"||section==="total")&&(
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                {(section==="ml"
                  ?[{label:`Away · ${row.away_abbr}`,key:"awayML"},{label:`Home · ${row.home_abbr}`,key:"homeML"}]
                  :[{label:"Over",key:"overOdds"},{label:"Under",key:"underOdds"}]
                ).map(({label,key})=>{
                  const bb=bestBook(odds||{},key);
                  const bestOdds=bb?odds?.[bb]?.[key]:null;
                  return(
                    <div key={key} style={{background:D.s2,borderRadius:10,padding:"10px 12px",border:`1px solid ${D.borderL}`}}>
                      <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:.8,marginBottom:4,textTransform:"uppercase"}}>{label}</div>
                      {section==="total"&&row.market_line&&(
                        <div style={{fontSize:20,fontWeight:700,color:D.t1,fontFamily:MONO,marginBottom:6}}>{key==="overOdds"?`Over ${row.market_line}`:`Under ${row.market_line}`}</div>
                      )}
                      {section==="ml"&&bestOdds&&(
                        <div style={{fontSize:20,fontWeight:700,color:parseInt(String(bestOdds).replace(/\+/,""))>0?D.green:D.t1,fontFamily:MONO,marginBottom:6}}>{fmtO(bestOdds)}</div>
                      )}
                      {BO.map(bk=><OddsBar key={bk} book={bk} odds={odds?.[bk]?.[key]} isBest={bb===bk}/>)}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={{padding:"0 14px 12px"}}>
            <div style={{fontSize:8,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:6}}>FAN PULSE</div>
            <div style={{display:"flex",gap:7}}>
              {[{l:"OVER",d:"over",c:D.blue},{l:"UNDER",d:"under",c:D.violet}].map(({l,d,c})=>{
                const v=pollData[d]||0, p=tv>0?Math.round(v/tv*100):50, voted=pollData.voted===d;
                return(
                  <button key={d} onClick={()=>onVote(row.game_id,d)}
                    style={{flex:1,padding:"8px",borderRadius:10,border:`1.5px solid ${voted?c:D.borderL}`,background:voted?`${c}10`:D.s2,cursor:"pointer",fontFamily:SANS}}>
                    <div style={{fontSize:8,fontWeight:700,color:voted?c:D.t3,marginBottom:2,letterSpacing:.8}}>{l}</div>
                    <div style={{fontSize:20,fontWeight:700,color:voted?c:D.t2,fontFamily:MONO}}>{p}%</div>
                    <div style={{fontSize:9,color:D.t3}}>{v} vote{v!==1?"s":""}</div>
                  </button>
                );
              })}
            </div>
          </div>
          <div style={{padding:"0 14px 12px"}}>
            <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
              {BO.map(bk=><BookLink key={bk} book={bk} isAvailable={isAvailable}/>)}
            </div>
          </div>
        </div>
      )}
      <div onClick={()=>setOpen(o=>!o)} style={{textAlign:"center",padding:"5px",borderTop:`1px solid ${D.borderL}`,fontSize:8,color:D.t2,cursor:"pointer",letterSpacing:1.2}}>
        {isFinal?"FINAL":(open?"▲ LESS":"▼ MORE")}
      </div>
    </div>
  );
};

const ResultsTab = ({yesterday, playsTrack})=>{
  const [historyFilter, setHistoryFilter] = useState('ALL');

  if(yesterday.loading||playsTrack?.loading) return(<div style={{display:"flex",flexDirection:"column",gap:8}}>{[1,2,3].map(i=><Skel key={i} h={80} r={12}/>)}</div>);

  // ── Unified pick history ──────────────────────────────────
  const oraclePicks = (yesterday.record||[]).map(r=>({
    ...r, type:'ORACLE',
    direction: r.pick?.split(' ')[0],
    line: parseFloat(r.pick?.split(' ')[1]||0),
  }));
  const playPicks = (playsTrack?.plays||[]).map(p=>({
    ...p, type:'PLAY',
    pick:`${p.direction} ${p.line}`,
    line: parseFloat(p.line||0),
    odds:-110, chaos_total:null,
  }));
  const allPicks = [...oraclePicks,...playPicks]
    .sort((a,b)=>new Date(b.date)-new Date(a.date));
  const filtered = historyFilter==='ALL'?allPicks
    :historyFilter==='WINS'?allPicks.filter(p=>p.result==='WIN')
    :historyFilter==='LOSSES'?allPicks.filter(p=>p.result==='LOSS')
    :allPicks.filter(p=>!p.result||p.result==='PENDING');
  const allSettled=allPicks.filter(p=>p.result&&p.result!=='PENDING');
  const totalWins=allSettled.filter(p=>p.result==='WIN').length;
  const totalLosses=allSettled.filter(p=>p.result==='LOSS').length;
  const totalPushes=allSettled.filter(p=>p.result==='PUSH').length;
  const totalPnl=allSettled.reduce((s,p)=>s+(parseFloat(p.pnl_units)||0),0);
  const totalWinPct=totalWins+totalLosses>0?Math.round(totalWins/(totalWins+totalLosses)*100):0;

  // Oracle-only stats (for Oracle track record card)
  const settled=yesterday.record.filter(r=>r.result&&r.result!=="PENDING");
  const wins=settled.filter(r=>r.result==="WIN").length;
  const losses=settled.filter(r=>r.result==="LOSS").length;
  const pushes=settled.filter(r=>r.result==="PUSH").length;
  const pnl=settled.reduce((s,r)=>s+(parseFloat(r.pnl_units)||0),0);
  const winRate=wins+losses>0?Math.round(wins/(wins+losses)*100):0;
  const roi=wins+losses>0?((pnl/(wins+losses))*100).toFixed(1):0;

  let streak=0,streakType=null;
  for(const r of settled){
    if(!streakType){streakType=r.result;streak=1;}
    else if(r.result===streakType) streak++;
    else break;
  }
  const streakLabel=streak>1?`${streak} ${streakType==="WIN"?"W":"L"} streak`:null;

  return(
    <div>
      <div style={{background:"linear-gradient(135deg,#061410,#0A0E1C)",border:"1px solid rgba(48,209,88,.2)",borderRadius:16,padding:18,marginBottom:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontSize:9,fontWeight:700,color:D.green,letterSpacing:2}}>⚡ ORACLE TRACK RECORD</div>
          {streakLabel&&(
            <div style={{fontSize:8,fontWeight:700,color:streakType==="WIN"?D.green:D.red,background:streakType==="WIN"?`${D.green}15`:`${D.red}15`,border:`1px solid ${streakType==="WIN"?D.green:D.red}30`,borderRadius:20,padding:"2px 8px",letterSpacing:.5}}>
              🔥 {streakLabel}
            </div>
          )}
        </div>
        <div style={{display:"flex",gap:0,marginBottom:14}}>
          {[
            {l:"Wins",   v:wins,          c:D.green},
            {l:"Losses", v:losses,        c:D.red},
            {l:"Pushes", v:pushes,        c:D.amber},
            {l:"Win %",  v:`${winRate}%`, c:winRate>=55?D.green:winRate>=50?D.amber:D.red},
          ].map((r,i)=>(
            <div key={r.l} style={{flex:1,textAlign:"center",borderRight:i<3?`1px solid ${D.borderL}`:"none",padding:"0 4px"}}>
              <div style={{fontSize:26,fontWeight:700,color:r.c,fontFamily:MONO,lineHeight:1}}>{r.v}</div>
              <div style={{fontSize:8,color:D.t2,letterSpacing:.5,marginTop:3}}>{r.l.toUpperCase()}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",borderTop:`1px solid ${D.borderL}`,paddingTop:12}}>
          <div>
            <div style={{fontSize:9,color:D.t3,letterSpacing:1,marginBottom:2}}>TOTAL P&L</div>
            <div style={{fontSize:22,fontWeight:700,fontFamily:MONO,color:pnl>=0?D.green:D.red}}>{pnl>=0?"+":""}{pnl.toFixed(2)} units</div>
          </div>
          <div style={{textAlign:"right"}}>
            <div style={{fontSize:9,color:D.t3,letterSpacing:1,marginBottom:2}}>ROI</div>
            <div style={{fontSize:22,fontWeight:700,fontFamily:MONO,color:parseFloat(roi)>=0?D.green:D.red}}>{parseFloat(roi)>=0?"+":""}{roi}%</div>
          </div>
        </div>
      </div>

      {/* ── THE PLAY TRACK RECORD ─────────────────────────── */}
      {playsTrack&&!playsTrack.loading&&playsTrack.plays.length>0&&(()=>{
        const settled  = playsTrack.plays.filter(p=>p.result&&p.result!=='PENDING');
        const pending  = playsTrack.plays.filter(p=>!p.result||p.result==='PENDING');
        const wins     = settled.filter(p=>p.result==='WIN').length;
        const losses   = settled.filter(p=>p.result==='LOSS').length;
        const pnl      = settled.reduce((s,p)=>s+(parseFloat(p.pnl_units)||0),0);
        const winRate  = wins+losses>0?Math.round(wins/(wins+losses)*100):0;

        // HIGH/ELITE split — win% excludes pushes (wins / decisive)
        const highElite   = settled.filter(p=>p.confidence_tier==='HIGH'||p.confidence_tier==='ELITE');
        const heWins      = highElite.filter(p=>p.result==='WIN').length;
        const heLosses    = highElite.filter(p=>p.result==='LOSS').length;
        const heWinRate   = heWins+heLosses>0?Math.round(heWins/(heWins+heLosses)*100):0;
        const eliteOnly      = settled.filter(p=>p.confidence_tier==='ELITE');
        const eliteWins      = eliteOnly.filter(p=>p.result==='WIN').length;
        const eliteLosses    = eliteOnly.filter(p=>p.result==='LOSS').length;
        const eliteWinRate   = eliteWins+eliteLosses>0?Math.round(eliteWins/(eliteWins+eliteLosses)*100):0;

        // OVER vs UNDER split — record is W-L and win% excludes pushes
        const overs       = settled.filter(p=>p.direction==='OVER');
        const unders      = settled.filter(p=>p.direction==='UNDER');
        const overWins    = overs.filter(p=>p.result==='WIN').length;
        const overLosses  = overs.filter(p=>p.result==='LOSS').length;
        const underWins   = unders.filter(p=>p.result==='WIN').length;
        const underLosses = unders.filter(p=>p.result==='LOSS').length;
        const overPct     = overWins+overLosses>0?Math.round(overWins/(overWins+overLosses)*100):0;
        const underPct    = underWins+underLosses>0?Math.round(underWins/(underWins+underLosses)*100):0;

        // Recent form — last 5 settled
        const last5       = settled.slice(0,5);

        // Streak
        let streak=0, streakType='';
        for(const p of settled){
          if(streak===0){ streakType=p.result; streak=1; }
          else if(p.result===streakType) streak++;
          else break;
        }
        const streakLabel = streak>1?(streakType==='WIN'?`${streak}W`:streakType==='LOSS'?`${streak}L`:''):'';

        const wc = r=>r>=55?D.green:r>=50?D.amber:D.red;

        return(
          <div style={{background:"linear-gradient(135deg,#0A0C1E,#0E0A1C)",border:"1px solid rgba(191,90,242,.2)",borderRadius:16,padding:18,marginBottom:14}}>

            {/* Header */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
              <div style={{fontSize:9,fontWeight:700,color:D.violet,letterSpacing:2}}>🎯 THE PLAY TRACK RECORD</div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                {streakLabel&&(
                  <div style={{fontSize:9,fontWeight:700,padding:"2px 8px",borderRadius:20,
                    background:streakType==='WIN'?`${D.green}20`:`${D.red}20`,
                    color:streakType==='WIN'?D.green:D.red,
                    border:`1px solid ${streakType==='WIN'?D.green:D.red}30`}}>
                    {streakLabel} STREAK
                  </div>
                )}
                {pending.length>0&&(
                  <div style={{fontSize:9,color:D.amber}}>⏳ {pending.length} pending</div>
                )}
              </div>
            </div>

            {/* Main stats row */}
            <div style={{display:"flex",gap:0,marginBottom:14}}>
              {[
                {l:"Wins",   v:wins,           c:D.green},
                {l:"Losses", v:losses,         c:D.red},
                {l:"Pushes", v:settled.filter(p=>p.result==='PUSH').length, c:D.amber},
                {l:"Win %",  v:`${winRate}%`,  c:wc(winRate)},
                {l:"HIGH+",  v:`${heWinRate}%`,c:wc(heWinRate)},
              ].map((r,i)=>(
                <div key={r.l} style={{flex:1,textAlign:"center",borderRight:i<4?`1px solid ${D.borderL}`:"none",padding:"0 4px"}}>
                  <div style={{fontSize:22,fontWeight:700,color:r.c,fontFamily:MONO,lineHeight:1}}>{r.v}</div>
                  <div style={{fontSize:8,color:D.t2,letterSpacing:.5,marginTop:3}}>{r.l.toUpperCase()}</div>
                </div>
              ))}
            </div>

            {/* OVER vs UNDER split */}
            <div style={{display:"flex",gap:8,marginBottom:14}}>
              {[
                {l:`OVER ${overWins}-${overLosses}`,   pct:overPct,  c:wc(overPct)},
                {l:`UNDER ${underWins}-${underLosses}`, pct:underPct, c:wc(underPct)},
              ].map(r=>(
                <div key={r.l} style={{flex:1,background:`${D.s2}`,borderRadius:8,padding:"6px 10px",
                  border:`1px solid ${D.borderL}`}}>
                  <div style={{fontSize:10,fontWeight:700,color:r.c,fontFamily:MONO}}>{r.pct}%</div>
                  <div style={{fontSize:8,color:D.t3,marginTop:2}}>{r.l}</div>
                </div>
              ))}
              {eliteOnly.length>0&&(
                <div style={{flex:1,background:`${D.s2}`,borderRadius:8,padding:"6px 10px",
                  border:`1px solid ${D.borderL}`}}>
                  <div style={{fontSize:10,fontWeight:700,color:wc(eliteWinRate),fontFamily:MONO}}>{eliteWinRate}%</div>
                  <div style={{fontSize:8,color:D.t3,marginTop:2}}>ELITE {eliteWins}-{eliteLosses}</div>
                </div>
              )}
            </div>

            {/* Recent form */}
            {last5.length>0&&(
              <div style={{marginBottom:14}}>
                <div style={{fontSize:8,color:D.t3,letterSpacing:1,marginBottom:6}}>RECENT FORM</div>
                <div style={{display:"flex",gap:4}}>
                  {last5.map((p,i)=>{
                    const c=p.result==='WIN'?D.green:p.result==='LOSS'?D.red:D.amber;
                    return(
                      <div key={i} style={{flex:1,height:28,borderRadius:6,background:`${c}20`,
                        border:`1px solid ${c}40`,display:"flex",alignItems:"center",
                        justifyContent:"center",fontSize:9,fontWeight:700,color:c}}>
                        {p.result==='WIN'?'W':p.result==='LOSS'?'L':'P'}
                      </div>
                    );
                  })}
                  {[...Array(Math.max(0,5-last5.length))].map((_,i)=>(
                    <div key={`e${i}`} style={{flex:1,height:28,borderRadius:6,
                      background:D.s2,border:`1px solid ${D.borderL}`}}/>
                  ))}
                </div>
              </div>
            )}

            {/* P&L and sample */}
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
              borderTop:`1px solid ${D.borderL}`,paddingTop:10}}>
              <div>
                <div style={{fontSize:9,color:D.t3,letterSpacing:1,marginBottom:2}}>TOTAL P&L</div>
                <div style={{fontSize:20,fontWeight:700,fontFamily:MONO,color:pnl>=0?D.green:D.red}}>
                  {pnl>=0?"+":""}{pnl.toFixed(2)}u
                </div>
              </div>
              <div style={{textAlign:"right"}}>
                <div style={{fontSize:9,color:D.t3,letterSpacing:1,marginBottom:2}}>RESULTS</div>
                <div style={{fontSize:20,fontWeight:700,fontFamily:MONO,color:D.t2}}>
                  {settled.length} settled
                </div>
              </div>
            </div>

          </div>
        );
      })()}

      {/* ── COMBINED STATS BAR ────────────────────────────── */}
      <div style={{background:D.s2,borderRadius:12,padding:"12px 14px",marginBottom:14,
        border:`1px solid ${D.borderL}`}}>
        <div style={{fontSize:8,fontWeight:700,color:D.t3,letterSpacing:1.5,marginBottom:10}}>
          COMBINED RECORD (ORACLE + THE PLAY)
        </div>
        <div style={{display:"flex",gap:0,marginBottom:10}}>
          {[
            {l:"Wins",   v:totalWins,          c:D.green},
            {l:"Losses", v:totalLosses,         c:D.red},
            {l:"Pushes", v:totalPushes,         c:D.amber},
            {l:"Win %",  v:`${totalWinPct}%`,   c:totalWinPct>=55?D.green:totalWinPct>=50?D.amber:D.red},
          ].map((r,i)=>(
            <div key={r.l} style={{flex:1,textAlign:"center",borderRight:i<3?`1px solid ${D.borderL}`:"none"}}>
              <div style={{fontSize:20,fontWeight:700,color:r.c,fontFamily:MONO,lineHeight:1}}>{r.v}</div>
              <div style={{fontSize:8,color:D.t2,letterSpacing:.5,marginTop:2}}>{r.l.toUpperCase()}</div>
            </div>
          ))}
        </div>
        <div style={{display:"flex",justifyContent:"space-between",borderTop:`1px solid ${D.borderL}`,paddingTop:8}}>
          <div style={{fontSize:9,color:D.t3}}>{allSettled.length} settled picks</div>
          <div style={{fontSize:13,fontWeight:700,fontFamily:MONO,color:totalPnl>=0?D.green:D.red}}>
            {totalPnl>=0?"+":""}{totalPnl.toFixed(2)}u
          </div>
        </div>
      </div>

      {/* ── PICK HISTORY FILTERS ──────────────────────────── */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <div style={{fontSize:9,fontWeight:700,color:D.t3,letterSpacing:1.5}}>PICK HISTORY</div>
        <div style={{display:"flex",gap:4}}>
          {['ALL','WINS','LOSSES','PENDING'].map(f=>(
            <button key={f} onClick={()=>setHistoryFilter(f)}
              style={{padding:"3px 8px",borderRadius:12,fontSize:9,fontWeight:historyFilter===f?700:400,
                cursor:"pointer",fontFamily:SANS,
                background:historyFilter===f?"rgba(255,255,255,.08)":"transparent",
                border:`1px solid ${historyFilter===f?D.border:D.borderL}`,
                color:historyFilter===f?D.t1:D.t3}}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* ── UNIFIED PICK ROWS ─────────────────────────────── */}
      <div style={{display:"flex",flexDirection:"column",gap:8}}>
        {filtered.length===0&&(
          <div style={{textAlign:"center",padding:30,color:D.t3,fontSize:12}}>
            No {historyFilter.toLowerCase()} picks yet
          </div>
        )}
        {filtered.map((r,i)=>{
          const win=r.result==="WIN", loss=r.result==="LOSS",
                push=r.result==="PUSH", pending=!r.result||r.result==="PENDING";
          const col=win?D.green:loss?D.red:push?D.amber:D.t3;
          const pnlVal=parseFloat(r.pnl_units||0);
          const actual=parseFloat(r.actual_total||0);
          const line=r.line||parseFloat(r.pick?.split(" ")[1]||0);
          const isOracle=r.type==='ORACLE';
          const typeColor=isOracle?D.blue:D.violet;
          const typeLabel=isOracle?'🔮 ORACLE':'🎯 THE PLAY';
          const fmtDate=new Date(r.date+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
          const covered=r.direction==='OVER'||r.pick?.startsWith('OVER')?actual>line:actual<line;
          return(
            <div key={`${r.type}-${i}`} style={{background:D.s1,
              border:`1px solid ${win?`${D.green}20`:loss?`${D.red}20`:D.borderL}`,
              borderRadius:14,overflow:"hidden"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                padding:"8px 14px 6px",borderBottom:`1px solid ${D.borderL}`}}>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  {/* Type badge */}
                  <span style={{fontSize:8,fontWeight:700,color:typeColor,
                    background:`${typeColor}15`,border:`1px solid ${typeColor}30`,
                    borderRadius:10,padding:"2px 7px",letterSpacing:.5}}>
                    {typeLabel}
                  </span>
                  {isOracle&&<ConfBadge tier={r.confidence_tier}/>}
                </div>
                <span style={{fontSize:11,fontWeight:700,color:col,
                  background:`${col}15`,border:`1px solid ${col}30`,
                  borderRadius:20,padding:"2px 9px"}}>
                  {win?"✓ WIN":loss?"✗ LOSS":push?"◌ PUSH":"⏳ PENDING"}
                </span>
              </div>
              <div style={{padding:"10px 14px"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:pending?0:8}}>
                  <div>
                    <div style={{fontSize:11,color:D.t3,marginBottom:2}}>
                      {r.away_abbr&&r.home_abbr?`${r.away_abbr}@${r.home_abbr}`:r.game_id} · {fmtDate}
                    </div>
                    <div style={{fontSize:20,fontWeight:700,color:D.t1,letterSpacing:-.3}}>{r.pick}</div>
                    <div style={{fontSize:10,color:D.t2,marginTop:2}}>
                      Edge {parseFloat(r.edge_pct||0).toFixed(1)}%
                      {isOracle&&r.chaos_total?` · Chaos ${r.chaos_total}`:''}
                      {r.confidence_tier&&!isOracle?` · ${r.confidence_tier}`:''}
                    </div>
                  </div>
                  {!pending&&(
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:9,color:D.t3,marginBottom:2}}>FINAL</div>
                      <div style={{fontSize:22,fontWeight:700,color:col,fontFamily:MONO}}>{r.actual_total}</div>
                      <div style={{fontSize:9,color:col,marginTop:1}}>{covered?"✓ covered":"✗ missed"}</div>
                    </div>
                  )}
                </div>
                {!pending&&(
                  <div style={{display:"flex",justifyContent:"space-between",
                    borderTop:`1px solid ${D.borderL}`,paddingTop:8}}>
                    <div style={{fontSize:10,color:D.t2}}>1 unit</div>
                    <div style={{fontSize:14,fontWeight:700,fontFamily:MONO,
                      color:pnlVal>=0?D.green:D.red}}>
                      {pnlVal>=0?"+":""}{pnlVal.toFixed(2)}u
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div style={{textAlign:"center",padding:"20px 0 8px",fontSize:10,color:D.t3}}>
        Based on 1 unit flat betting · Results since launch
      </div>
    </div>
  );
};

// ── AI PICKS TAB ───────────────────────────────────────────────
const AiTab = ({predictions,loading,boardGames,analyze,analyzing,aiData})=>{
  const [parlayText,setParlayText]=useState(null);
  const [buildingParlay,setBuildingParlay]=useState(false);

  const topGames=boardGames?.filter(g=>g.status==="scheduled"&&parseFloat(g.edge_pct||0)>0).sort((a,b)=>parseFloat(b.edge_pct)-parseFloat(a.edge_pct)).slice(0,5)||[];

  const parlayLegs=boardGames?.filter(g=>{
    if(g.status!=="scheduled") return false;
    const ep=parseFloat(g.edge_pct||0);
    const isOver  = ep > 4  && g.confidence_tier!=="PASS" && g.confidence_tier!=="LOW" && g.confidence_tier!=null;
    const isUnder = ep < -4 && g.confidence_tier!=="PASS" && g.confidence_tier!=="LOW" && g.confidence_tier!=null;
    if(!isOver && !isUnder) return false;
    if((g.chaos_total||0)>=60) return false;
    const etHour=new Date(g.scheduled_time).toLocaleString("en-US",{hour:"numeric",hour12:false,timeZone:"America/New_York"});
    if(parseInt(etHour)>=21) return false;
    return true;
  }).sort((a,b)=>Math.abs(parseFloat(b.edge_pct||0))-Math.abs(parseFloat(a.edge_pct||0))).slice(0,3)||[];

  const buildParlay=async()=>{
    if(!parlayLegs.length) return;
    setBuildingParlay(true);
    try{
      const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-analysis`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":process.env.REACT_APP_SUPABASE_ANON_KEY},
        body:JSON.stringify({
          away:"PARLAY_BUILD", home:"PARLAY_BUILD",
          // Team abbreviation glossary — ATH = Oakland/Sacramento Athletics (formerly OAK)
          glossary:"ATH=Athletics(formerly OAK), WSH=Nationals, TB=Rays, KC=Royals, MIL=Brewers, ARI=Diamondbacks",
          legs:parlayLegs.map(g=>({
            matchup:`${g.away_abbr}@${g.home_abbr}`,
            direction:parseFloat(g.edge_pct||0)>=0?"OVER":"UNDER",
            line:g.market_line,
            edge:`${parseFloat(g.edge_pct||0)>=0?"+":""}${parseFloat(g.edge_pct||0).toFixed(1)}%`,
            confidence:g.confidence_tier,
            chaos:g.chaos_total||0,
            projection:g.projected_total,
          })),
          instruction:`Analyze this ${parlayLegs.length}-leg parlay. For each leg state the matchup, direction, and edge. Explain why these games pair well or poorly. Give a combined payout estimate. Recommend whether to play all legs or trim to the strongest. End with: The parlay: [specific bets]. Keep it under 5 sentences.`,
        }),
      });
      const data=await res.json();
      setParlayText(data.analysis||"Could not build parlay.");
    }catch{setParlayText("Could not connect.");}
    setBuildingParlay(false);
  };

  if(loading) return(<div style={{display:"flex",flexDirection:"column",gap:8}}>{[1,2,3].map(i=><Skel key={i} h={80} r={12}/>)}</div>);

  return(
    <div>
      {parlayLegs.length>=2&&(
        <div style={{background:"linear-gradient(135deg,#0A0A14,#10102A)",border:"1px solid rgba(191,90,242,.25)",borderRadius:16,padding:16,marginBottom:16}}>
          <div style={{fontSize:9,fontWeight:600,color:D.violet,letterSpacing:2,marginBottom:4}}>🎰 AI PARLAY BUILDER</div>
          <div style={{fontSize:9,color:D.t3,marginBottom:10}}>HIGH/MEDIUM confidence · Edge &gt;4% · Chaos &lt;60 · Before 9pm ET</div>
          <div style={{marginBottom:10}}>
            {parlayLegs.map((g,i)=>(
              <div key={g.game_id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"5px 0",borderBottom:i<parlayLegs.length-1?`1px solid ${D.borderL}`:"none"}}>
                <div>
                  <span style={{fontSize:12,fontWeight:600,color:D.t1}}>{g.away_abbr}@{g.home_abbr}</span>
                  <span style={{fontSize:9,color:D.t3,marginLeft:8}}>{g.confidence_tier} · Chaos {g.chaos_total||0}</span>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:10,fontWeight:600,color:parseFloat(g.edge_pct||0)>=0?D.green:D.violet}}>
                    {parseFloat(g.edge_pct||0)>=0?"OVER":"UNDER"} {g.market_line}
                  </span>
                  <span style={{fontSize:11,fontWeight:700,color:parseFloat(g.edge_pct||0)>=0?D.blue:D.violet}}>{parseFloat(g.edge_pct||0)>=0?"+":""}{parseFloat(g.edge_pct).toFixed(1)}%</span>
                </div>
              </div>
            ))}
          </div>
          {!parlayText?(
            <button onClick={buildParlay} disabled={buildingParlay}
              style={{width:"100%",padding:"10px",borderRadius:10,border:"none",background:D.violet,color:"#fff",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:SANS}}>
              {buildingParlay?"Building Parlay…":"🧠 Build AI Parlay Read →"}
            </button>
          ):(
            <div style={{background:"rgba(191,90,242,.08)",borderRadius:10,padding:"10px 12px",border:`1px solid rgba(191,90,242,.2)`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                <div style={{fontSize:8,fontWeight:700,color:D.violet,letterSpacing:1.2}}>AI PARLAY READ</div>
                <button onClick={()=>setParlayText(null)} style={{fontSize:9,color:D.t3,background:"transparent",border:`1px solid ${D.borderL}`,borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:SANS}}>↺ Rebuild</button>
              </div>
              <div style={{fontSize:13,color:D.t2,lineHeight:1.75}}>{parlayText}</div>
            </div>
          )}
        </div>
      )}

      <div style={{marginBottom:16}}>
        <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:10}}>🧠 TODAY'S SHARP READS</div>
        {topGames.length===0?(
          <div style={{background:D.s1,borderRadius:12,padding:"16px",border:`1px solid ${D.borderL}`,fontSize:12,color:D.t3,textAlign:"center"}}>Sharp angles surface after 10am ET — check back then.</div>
        ):(
          topGames.map(g=>{
            const id=g.game_id; const text=aiData[id];
            return(
              <div key={id} style={{background:D.s1,borderRadius:12,padding:"12px 14px",marginBottom:8,border:`1px solid ${D.borderL}`}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <span style={{fontSize:15,fontWeight:700,color:D.t1}}>{g.away_abbr}@{g.home_abbr}</span>
                    <ConfBadge tier={g.confidence_tier}/>
                  </div>
                  <div style={{textAlign:"right"}}>
                    <div style={{fontSize:11,fontWeight:700,color:D.blue,fontFamily:MONO}}>+{parseFloat(g.edge_pct||0).toFixed(1)}%</div>
                    <div style={{fontSize:9,color:D.t3}}>{formatTime(g.scheduled_time)}</div>
                  </div>
                </div>
                {!text?(
                  <button onClick={()=>analyze(g)}
                    style={{width:"100%",padding:"8px",borderRadius:8,border:`1px solid ${D.border}`,background:"transparent",color:D.t2,cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:SANS}}>
                    {analyzing===id?"Reading the line…":"Generate AI Read →"}
                  </button>
                ):(
                  <div>
                    <div style={{fontSize:12,color:D.t2,lineHeight:1.75,marginBottom:8}}>{text}</div>
                    <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:D.t3}}>
                      <span>{parseFloat(g.edge_pct||0)>=0?"OVER":"UNDER"} {g.market_line||"—"} · Chaos {g.chaos_total||"—"}/100</span>
                      <span style={{color:D.amber}}>⏳ Result pending</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {predictions.length>0&&(
        <div>
          <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:10}}>PREVIOUS AI READS</div>
          <div style={{background:D.s1,border:`1px solid ${D.border}`,borderRadius:14,overflow:"hidden"}}>
            {predictions.map((p,i)=>(
              <div key={p.id} style={{padding:"12px 14px",borderBottom:i<predictions.length-1?`1px solid ${D.borderL}`:"none"}}>
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                  <span style={{fontSize:13,fontWeight:600,color:D.t1}}>{p.away_abbr}@{p.home_abbr}</span>
                  <span style={{fontSize:11,fontWeight:700,color:p.result==="WIN"?D.green:p.result==="LOSS"?D.red:p.result==="PUSH"?D.amber:D.t3}}>
                    {p.result==="WIN"?"✓ WIN":p.result==="LOSS"?"✗ LOSS":p.result==="PUSH"?"◌ PUSH":"⏳ PENDING"}
                  </span>
                </div>
                <div style={{fontSize:11,color:D.t2,lineHeight:1.65,marginBottom:4}}>{p.analysis}</div>
                <div style={{fontSize:9,color:D.t3}}>{p.date}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── TOP 3 PLAYS STRIP ─────────────────────────────────────────
const Top3Strip = ({games,onGameClick})=>{
  const upcoming = (games||[]).filter(g=>g.status==="scheduled"&&g.confidence_tier&&g.confidence_tier!=="PASS"&&g.confidence_tier!=null);
  const oddsReady = upcoming.some(g=>parseFloat(g.edge_pct||0)!==0);

  const used = new Set();
  const byEdge = [...upcoming].filter(g=>parseFloat(g.edge_pct||0)>0).sort((a,b)=>parseFloat(b.edge_pct)-parseFloat(a.edge_pct));
  const byChaos = [...upcoming].sort((a,b)=>(a.chaos_total||100)-(b.chaos_total||100));

  const bestOver = byEdge[0] || null;
  if(bestOver) used.add(bestOver.game_id);
  const bestTotal = byEdge.find(g=>!used.has(g.game_id)) || null;
  if(bestTotal) used.add(bestTotal.game_id);
  const lowChaos = byChaos.find(g=>!used.has(g.game_id)) || null;
  if(lowChaos) used.add(lowChaos.game_id);

  const cards = [
    bestOver  && {badge:"BEST EDGE",  gameId:bestOver.game_id,  matchup:`${bestOver.away_abbr}@${bestOver.home_abbr}`,   pick:`OVER ${bestOver.market_line||"—"}`,  sub:`Edge +${parseFloat(bestOver.edge_pct||0).toFixed(1)}%`,  color:D.green},
    bestTotal && {badge:"HIGH CONF",  gameId:bestTotal.game_id, matchup:`${bestTotal.away_abbr}@${bestTotal.home_abbr}`, pick:bestTotal.confidence_tier,            sub:`Edge +${parseFloat(bestTotal.edge_pct||0).toFixed(1)}%`, color:D.cyan},
    lowChaos  && {badge:"LOW CHAOS",  gameId:lowChaos.game_id,  matchup:`${lowChaos.away_abbr}@${lowChaos.home_abbr}`,  pick:`CHAOS ${lowChaos.chaos_total||"—"}`,  sub:`Stable conditions`,                                      color:D.amber},
  ].filter(Boolean);

  if(!cards.length) return null;

  return(
    <div style={{marginBottom:12}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
        <div style={{fontSize:8,fontWeight:700,color:D.green,letterSpacing:1.5}}>✦ TODAY'S TOP PLAYS</div>
        <div style={{fontSize:7,fontWeight:600,color:oddsReady?D.green:D.amber,background:oddsReady?`${D.green}15`:`${D.amber}15`,border:`1px solid ${oddsReady?D.green:D.amber}30`,borderRadius:20,padding:"2px 8px",letterSpacing:.5}}>
          {oddsReady?"UPDATED":"UPDATING"}
        </div>
      </div>
      <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:4}}>
        {cards.map((c,i)=>(
          <div key={i} onClick={()=>onGameClick&&onGameClick(c.gameId)}
            style={{flex:"0 0 auto",width:100,background:D.s1,border:`1px solid ${c.color}25`,borderRadius:12,padding:"9px 10px",cursor:"pointer",transition:"transform .15s,border-color .15s",activeOpacity:.8}}
            onMouseDown={e=>e.currentTarget.style.transform="scale(.97)"}
            onMouseUp={e=>e.currentTarget.style.transform="scale(1)"}
            onTouchStart={e=>e.currentTarget.style.transform="scale(.97)"}
            onTouchEnd={e=>e.currentTarget.style.transform="scale(1)"}>
            <div style={{fontSize:7,fontWeight:700,color:c.color,letterSpacing:.8,marginBottom:4}}>{c.badge}</div>
            <div style={{fontSize:12,fontWeight:700,color:D.t1,marginBottom:2}}>{c.matchup}</div>
            <div style={{fontSize:9,color:D.t2,marginBottom:4}}>{c.pick}</div>
            <div style={{fontSize:8,fontWeight:600,color:c.color}}>{c.sub}</div>
            <div style={{fontSize:7,color:D.t3,marginTop:4,letterSpacing:.3}}>Tap for details ↓</div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── BOARD CONTENT ──────────────────────────────────────────────
const BoardContent = React.memo(({oracle,board,polls,vote,aiData,analyze,analyzing,
  marketIntelData,analyzeMarketIntel,marketIntelLoading,
  postGameSummary,fetchPostGameSummary,summaryLoading,
  filter,setFilter,sort,setSort,selDate,setSelDate,
  processedGames,liveGames,upcoming,finals,isAvailable,
  onTip,onGameClick,highlightId,gameRefs,gameCounts,trackRecord})=>(
  <div>
    <OracleHero oracle={oracle} boardGames={board.games} onTip={onTip} isAvailable={isAvailable} trackRecord={trackRecord} liveGames={liveGames}/>
    <Top3Strip games={board.games} onGameClick={onGameClick}/>
    <SharpSummary games={board.games} lastUpdated={board.lastUpdated} onGameClick={onGameClick}/>
    <HowItWorks onTip={onTip}/>
    <WeatherAlert games={board.games}/>
    <FilterBar filter={filter} setFilter={setFilter} sort={sort} setSort={setSort}/>
    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
      <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.5}}>
        {selDate===new Date().toLocaleDateString("en-CA",{timeZone:"America/New_York"})
          ?`TODAY'S SLATE · ${processedGames.length} GAMES`
          :`${new Date(selDate+'T12:00:00').toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})} · ${processedGames.length} GAMES`}
      </div>
      <button onClick={board.refresh} style={{fontSize:10,color:D.t2,background:D.s1,border:`1px solid ${D.borderL}`,borderRadius:6,padding:"3px 8px",cursor:"pointer",fontFamily:SANS}}>↻</button>
    </div>
    {board.error&&(<div style={{background:"rgba(255,69,58,.08)",border:"1px solid rgba(255,69,58,.2)",borderRadius:10,padding:"8px 12px",marginBottom:10,fontSize:14,color:D.red}}>⚠ {board.error}</div>)}
    {board.loading?(
      [1,2,3].map(i=>(<div key={i} style={{background:D.s1,borderRadius:16,padding:16,marginBottom:10,border:`1px solid ${D.border}`}}><Skel h={22} w="50%" r={5}/><div style={{marginTop:6}}><Skel h={10} w="35%" r={4}/></div><div style={{marginTop:12}}><Skel h={48} r={8}/></div></div>))
    ):(
      <>
        {liveGames.length>0&&(
          <div>
            <div style={{fontSize:8,fontWeight:600,color:D.live,letterSpacing:1.5,marginBottom:6,display:"flex",alignItems:"center",gap:5}}><span style={{fontSize:7,animation:"pulse 2s infinite",display:"inline-block"}}>●</span> LIVE NOW</div>
            {liveGames.map((row,i)=>{const ref=gameRefs.current[row.game_id]||(gameRefs.current[row.game_id]=React.createRef());return <GameCard key={row.game_id} row={row} index={i} odds={board.odds[row.game_id]||{}} pollData={polls[row.game_id]||{over:0,under:0,voted:null}} onVote={vote} aiText={aiData[row.game_id]} onAnalyze={analyze} analyzing={analyzing} marketIntelData={marketIntelData[row.game_id]} onMarketIntel={analyzeMarketIntel} marketIntelLoading={marketIntelLoading[row.game_id]} postGameSummary={postGameSummary[row.game_id]} onPostGameSummary={fetchPostGameSummary} summaryLoading={summaryLoading[row.game_id]} isAvailable={isAvailable} onTip={onTip} isHighlighted={highlightId===row.game_id} cardRef={ref}/>;})}</div>
        )}
        {upcoming.length>0&&(
          <div>
            {liveGames.length>0&&<div style={{fontSize:8,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:6,marginTop:14}}>UPCOMING</div>}
            {upcoming.map((row,i)=>{const ref=gameRefs.current[row.game_id]||(gameRefs.current[row.game_id]=React.createRef());return <GameCard key={row.game_id} row={row} index={i} odds={board.odds[row.game_id]||{}} pollData={polls[row.game_id]||{over:0,under:0,voted:null}} onVote={vote} aiText={aiData[row.game_id]} onAnalyze={analyze} analyzing={analyzing} marketIntelData={marketIntelData[row.game_id]} onMarketIntel={analyzeMarketIntel} marketIntelLoading={marketIntelLoading[row.game_id]} postGameSummary={postGameSummary[row.game_id]} onPostGameSummary={fetchPostGameSummary} summaryLoading={summaryLoading[row.game_id]} isAvailable={isAvailable} onTip={onTip} isHighlighted={highlightId===row.game_id} cardRef={ref}/>;})}</div>
        )}
        {finals.length>0&&(
          <div>
            <div style={{fontSize:8,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:6,marginTop:14}}>FINAL · {finals.length} GAME{finals.length>1?"S":""}</div>
            {finals.map((row,i)=>{const ref=gameRefs.current[row.game_id]||(gameRefs.current[row.game_id]=React.createRef());return <GameCard key={row.game_id} row={row} index={i} odds={board.odds[row.game_id]||{}} pollData={polls[row.game_id]||{over:0,under:0,voted:null}} onVote={vote} aiText={aiData[row.game_id]} onAnalyze={analyze} analyzing={analyzing} marketIntelData={marketIntelData[row.game_id]} onMarketIntel={analyzeMarketIntel} marketIntelLoading={marketIntelLoading[row.game_id]} postGameSummary={postGameSummary[row.game_id]} onPostGameSummary={fetchPostGameSummary} summaryLoading={summaryLoading[row.game_id]} isAvailable={isAvailable} onTip={onTip} isHighlighted={highlightId===row.game_id} cardRef={ref}/>;})}</div>
        )}
        {processedGames.length===0&&(<div style={{textAlign:"center",padding:"60px 20px",color:D.t2}}><div style={{fontSize:28,marginBottom:8}}>⚾</div><div style={{fontSize:14}}>No games match your filter.</div></div>)}
      </>
    )}
    <div style={{marginTop:20,paddingTop:16,borderTop:`1px solid ${D.borderL}`}}>
      <ScheduleStrip selectedDate={selDate} onSelectDate={setSelDate} gameCounts={gameCounts} todayGames={processedGames.length}/>
      <div style={{marginTop:8,fontSize:11,color:D.t3,textAlign:"center",padding:"12px 0"}}>Tap a date to preview upcoming matchups.</div>
    </div>
  </div>
));

// ── GAME INTEL CONTENT ─────────────────────────────────────────
const GameIntelContent = React.memo(({live,batters,pitchers,hasActiveGames})=>(
  <div>
    <div style={{display:"flex",alignItems:"center",gap:7,marginBottom:12}}>
      <span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite",display:"inline-block"}}>●</span>
      <span style={{fontSize:11,color:D.live,fontWeight:600,letterSpacing:.5}}>GAME INTEL · Updates every 30s</span>
    </div>
    {live.loading?(
      [1,2].map(i=>(<div key={i} style={{background:D.s1,borderRadius:16,padding:16,marginBottom:10,border:`1px solid ${D.border}`}}><Skel h={20} w="45%" r={5}/><div style={{marginTop:10}}><Skel h={80} r={8}/></div></div>))
    ):live.games.length===0?(
      <div>
        <div style={{textAlign:"center",paddingTop:40,paddingBottom:24,color:D.t2}}>
          <div style={{fontSize:28,marginBottom:10}}>🏟️</div>
          <div style={{fontSize:14,marginBottom:4}}>No live games right now.</div>
          <div style={{fontSize:11,color:D.t3}}>Game Intel activates when first pitch is thrown.</div>
        </div>
        <div>
          {/* Only show Pitcher Tank when scheduled or live games remain today */}
          {hasActiveGames&&(
            <>
              <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.2,marginBottom:8}}>⚾ PITCHER TANK — TODAY'S STARTERS</div>
              {pitchers.length>0?(
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16}}>
                  {pitchers.slice(0,4).map((p,i)=><PitcherTank key={p.name+i} pitcher={p} delay={i*100}/>)}
                </div>
              ):(
                <div style={{background:D.s2,borderRadius:10,padding:"12px 14px",border:`1px solid ${D.borderL}`,fontSize:12,color:D.t3,textAlign:"center",marginBottom:16}}>Starting pitchers confirmed closer to first pitch.</div>
              )}
            </>
          )}
          <div style={{fontSize:9,fontWeight:600,color:D.t2,letterSpacing:1.2,marginBottom:8}}>💣 HR WATCH</div>
          <div style={{background:D.s2,borderRadius:12,padding:"10px 12px",border:`1px solid ${D.borderL}`,fontSize:11,color:D.t2,lineHeight:1.65,marginBottom:8}}>
            Only showing players at or beyond their personal HR average. Adjusted for today's pitching matchup, park factor, and platoon splits.
            <span style={{color:D.amber}}> Amber</span> = at avg · <span style={{color:D.red}}> Red</span> = overdue.
            <span style={{color:D.t3}}> HR dates sourced from MLB Stats API — may reflect a 24–48 hour delay.</span>
          </div>
          {batters.filter(b=>(b.adjusted_due||b.due)>=100 && b.is_active!==false).length>0&&(
            <div style={{background:"rgba(255,69,58,.06)",border:"1px solid rgba(255,69,58,.2)",borderRadius:12,padding:"10px 12px",marginBottom:8}}>
              <div style={{fontSize:8,fontWeight:700,color:D.red,letterSpacing:1.5,marginBottom:6}}>🚨 DUE NOW</div>
              {batters.filter(b=>(b.adjusted_due||b.due)>=100 && b.is_active!==false).slice(0,3).map(b=>(
                <div key={b.name} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"3px 0"}}>
                  <div><span style={{fontSize:14,fontWeight:600,color:D.t1}}>{b.name}</span><span style={{fontSize:10,color:D.t2,marginLeft:6}}>{b.team}</span></div>
                  <span style={{fontSize:11,fontWeight:700,color:(b.adjusted_due||b.due)>=130?D.red:D.amber,fontFamily:MONO}}>{b.adjusted_due||b.due}%</span>
                </div>
              ))}
            </div>
          )}
          <div style={{display:"flex",flexDirection:"column",gap:7}}>
            {(()=>{
              const active=[...batters].filter(b=>(b.adjusted_due||b.due)>=100&&b.is_active!==false).sort((a,b)=>(b.adjusted_due||b.due)-(a.adjusted_due||a.due)||b.since-a.since);
              const il=[...batters].filter(b=>b.is_active===false);
              return [...active,...il].map((b,i)=><HRCard key={b.name} batter={b} delay={i*60}/>);
            })()}
            {batters.filter(b=>(b.adjusted_due||b.due)>=100&&b.is_active!==false).length===0&&(
              <div style={{background:D.s2,borderRadius:12,padding:"16px",border:`1px solid ${D.borderL}`,textAlign:"center"}}>
                <div style={{fontSize:20,marginBottom:6}}>✅</div>
                <div style={{fontSize:13,color:D.t1,fontWeight:600,marginBottom:4}}>No players overdue today</div>
                <div style={{fontSize:11,color:D.t3}}>HR Watch updates daily at 9 AM. HR dates may reflect a 24–48 hour delay from the MLB Stats API.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    ):(
      [...live.games].sort((a,b)=>statusPriority(a.status)-statusPriority(b.status)||new Date(a.scheduled_time)-new Date(b.scheduled_time)||(a.game_number||1)-(b.game_number||1)).map(g=>(
        <LiveGameCard key={g.game_id} game={g}
          batters={batters.filter(b=>b.team===g.away_abbr||b.team===g.home_abbr)}/>
      ))
    )}
  </div>
));

const TABS=[
  {k:"board",     l:"Board",       icon:"⚾"},
  {k:"gameintel", l:"Intel",       icon:"🔬"},
  {k:"ai",        l:"Sharp Picks", icon:"🧠"},
  {k:"results",   l:"Results",     icon:"📋"},
];

export default function SharpInning() {
  const [tab,         setTab]         = useState("board");
  const [filter,      setFilter]      = useState("ALL");
  const [sort,        setSort]        = useState("time");
  const [selDate,     setSelDate]     = useState(new Date().toLocaleDateString("en-CA",{timeZone:"America/New_York"}));
  const [polls,       setPolls]       = useState({});
  const [aiData,      setAiData]      = useState({});
  const [analyzing,        setAnalyzing]        = useState(null);
  const [marketIntelData,    setMarketIntelData]    = useState({});
  const [marketIntelLoading, setMarketIntelLoading] = useState({});
  const [postGameSummary,    setPostGameSummary]    = useState({});
  const [summaryLoading,     setSummaryLoading]     = useState({});
  const [modal,       setModal]       = useState(null);
  const [tip,         setTip]         = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const gameRefs = useRef({});

  // Reset to today when app comes back to foreground after being backgrounded
  // This prevents the board from showing yesterday's games after midnight
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
        setSelDate(prev => prev !== todayStr ? todayStr : prev);
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const board          = useBoard(selDate);
  const oracleHook     = useOracle();
  const yesterday      = useYesterday();
  const playsTrack     = usePlays();
  const live           = useLiveGames();
  const batters        = useBatters();
  const todaysPitchers = useTodaysPitchers();
  const aiPreds        = useAiPredictions();
  const gameCounts     = useScheduleSummary();
  const { isAvailable } = useBookStates();

  const today = new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
  const showTip = useCallback((title,body)=>setTip({title,body}),[]);
  const hideTip = useCallback(()=>setTip(null),[]);

  const handleGameClick = useCallback((gameId)=>{
    setTab("board");
    setHighlightId(gameId);
    setTimeout(()=>setHighlightId(null),3500);
  },[]);

  useEffect(()=>{
    if(!board.games.length) return;
    board.games.forEach(async g=>{
      try{
        const votes=await fetchPollVotes(g.game_id);
        setPolls(p=>({...p,[g.game_id]:{over:votes.over_votes||0,under:votes.under_votes||0,voted:p[g.game_id]?.voted||null}}));
      }catch{}
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[board.games.length]);

  const vote=useCallback(async(gid,dir)=>{
    setPolls(p=>{
      const g=p[gid]||{over:0,under:0,voted:null};
      if(g.voted) return p;
      const updated={...g,[dir]:g[dir]+1,voted:dir};
      upsertPollVote(gid,dir).catch(console.error);
      return{...p,[gid]:updated};
    });
  },[]);

  const analyzeMarketIntel=useCallback(async(row)=>{
    if(marketIntelLoading[row.game_id]) return;
    setMarketIntelLoading(p=>({...p,[row.game_id]:true}));
    try{
      const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/market-analysis`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":process.env.REACT_APP_SUPABASE_ANON_KEY},
        body:JSON.stringify({
          away:row.away_abbr, home:row.home_abbr,
          venue:row.venue_name,
          line_open:row.snap_line_open,
          line_current:row.snap_line_current,
          line_movement:row.snap_line_movement,
          book_spread:row.snap_book_spread,
        }),
      });
      const data=await res.json();
      setMarketIntelData(p=>({...p,[row.game_id]:data}));
    }catch(err){
      console.error('[MARKET INTEL]',err);
    }finally{
      setMarketIntelLoading(p=>({...p,[row.game_id]:false}));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[]);

  const fetchPostGameSummary=useCallback(async(row)=>{
    if(summaryLoading[row.game_id]||postGameSummary[row.game_id]) return;
    setSummaryLoading(p=>({...p,[row.game_id]:true}));
    try{
      const ep=parseFloat(row.edge_pct||0);
      const isOver=ep>0;
      const totalRuns=(parseInt(row.away_runs||0)+parseInt(row.home_runs||0));
      const won=isOver?totalRuns>parseFloat(row.market_line):totalRuns<parseFloat(row.market_line);
      const pushed=totalRuns===parseFloat(row.market_line);
      const result=pushed?'PUSH':won?'WIN':'LOSS';
      const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/post-game-summary`,{
        method:'POST',
        headers:{'Content-Type':'application/json','apikey':process.env.REACT_APP_SUPABASE_ANON_KEY},
        body:JSON.stringify({
          away:row.away_abbr, home:row.home_abbr,
          direction:isOver?'OVER':'UNDER',
          line:row.market_line,
          actual_total:totalRuns,
          away_runs:row.away_runs, home_runs:row.home_runs,
          away_pitcher:row.away_pitcher, away_era:row.away_era,
          home_pitcher:row.home_pitcher, home_era:row.home_era,
          projected_total:row.projected_total,
          edge_pct:ep,
          temp_f:row.temp_f, wind_mph:row.wind_mph, wind_direction:row.wind_direction,
          result,
        }),
      });
      const data=await res.json();
      setPostGameSummary(p=>({...p,[row.game_id]:data.summary||''}));
    }catch(err){
      console.error('[POST-GAME]',err);
    }finally{
      setSummaryLoading(p=>({...p,[row.game_id]:false}));
    }
  },[summaryLoading,postGameSummary]);

  const analyze=useCallback(async(row)=>{
    const id=row.game_id||row.id;
    setAnalyzing(id);
    try{
      const res=await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/ai-analysis`,{
        method:"POST",
        headers:{"Content-Type":"application/json","apikey":process.env.REACT_APP_SUPABASE_ANON_KEY},
        body:JSON.stringify({
          away:row.away_abbr, home:row.home_abbr,
          chaos:row.chaos_total, edge:row.edge_pct,
          venue:row.venue_name,
          weather:`${row.temp_f||"—"}° ${Math.round(row.wind_mph||0)}mph ${row.wind_direction||""} · Rain ${row.rain_probability||0}%`,
          line:row.market_line,
          projection:row.projected_total,
          confidence:row.confidence_tier||"UNKNOWN",
          pitchers:row.away_pitcher&&row.home_pitcher?`${row.away_pitcher}${row.away_era!=null?` (ERA ${parseFloat(row.away_era).toFixed(2)})`:""}  vs  ${row.home_pitcher}${row.home_era!=null?` (ERA ${parseFloat(row.home_era).toFixed(2)})`:""}`:"TBD",
          line_movement:row.snap_line_movement!=null?`${row.snap_line_movement>=0?"+":""}${row.snap_line_movement} from open ${row.snap_line_open||""}`:null,
        }),
      });
      const data=await res.json();
      const text=data.analysis||data.error||"Unavailable.";
      setAiData(p=>({...p,[id]:text}));
      if(data.analysis){
        saveAiPrediction({game_id:id,away_abbr:row.away_abbr,home_abbr:row.home_abbr,analysis:data.analysis,play:data.analysis.match(/[Tt]he play[:\s]+(.+?)\.?\s*$/)?.[1]||data.analysis.match(/[Pp]lay[:\s]+(.+?)\.?\s*$/)?.[1]||null}).catch(console.error);
        aiPreds.refresh();
      }
    }catch{setAiData(p=>({...p,[id]:"Could not connect."}));}
    setAnalyzing(null);
  },[aiPreds]);

  const processedGames=(()=>{
    if(!board.games.length) return [];
    let games=[...board.games].filter(g=>g.status!=="postponed"); // exclude postponed from board
    if(filter==="PLAY"){
      games=games.filter(g=>{
        const ep=Math.abs(parseFloat(g.edge_pct||0));
        const notPass=g.confidence_tier!=="PASS"&&g.confidence_tier!=="LOW";
        if(g.status==="final") return ep>4&&notPass;
        return ep>4&&notPass&&(g.away_era!=null||g.home_era!=null);
      });
    } else if(filter!=="ALL") {
      games=games.filter(g=>g.confidence_tier===filter);
    }
    if(sort==="edge") games.sort((a,b)=>parseFloat(b.edge_pct||0)-parseFloat(a.edge_pct||0));
    else if(sort==="chaos") games.sort((a,b)=>(b.chaos_total||0)-(a.chaos_total||0));
    else if(sort==="play"){
      // THE PLAY games first — edge > 4, not PASS, ERA confirmed (away_era not null)
      // Within THE PLAY group: sorted by edge_pct descending
      // Non-THE PLAY games follow sorted by time
      const hasPlay=g=>Math.abs(parseFloat(g.edge_pct||0))>4&&g.confidence_tier!=="PASS"&&g.away_era!=null;
      games.sort((a,b)=>{
        const ap=hasPlay(a)?1:0, bp=hasPlay(b)?1:0;
        if(bp!==ap) return bp-ap;
        if(ap&&bp) return Math.abs(parseFloat(b.edge_pct||0))-Math.abs(parseFloat(a.edge_pct||0));
        return statusPriority(a.status)-statusPriority(b.status)||new Date(a.scheduled_time)-new Date(b.scheduled_time);
      });
    }
    else games.sort((a,b)=>statusPriority(a.status)-statusPriority(b.status)||new Date(a.scheduled_time)-new Date(b.scheduled_time)||(a.game_number||1)-(b.game_number||1));
    return games;
  })();

  const liveGames=processedGames.filter(g=>g.status==="live");
  // Exclude games already shown in liveGames to prevent duplicates during status transitions
  const liveIds=new Set(liveGames.map(g=>String(g.game_id)));
  const upcoming=processedGames.filter(g=>g.status==="scheduled"&&!liveIds.has(String(g.game_id)));
  const finals=processedGames.filter(g=>g.status==="final"&&!liveIds.has(String(g.game_id)));
  // Postponed games explicitly excluded — status="postponed" wont match any section above

  return(
    <div style={{minHeight:"100vh",background:D.bg,fontFamily:SANS,color:D.t1}}>
      <style>{CSS}</style>
      <div className="app-shell">
        <div className="top-nav glass" style={{position:"sticky",top:0,zIndex:100,borderBottom:`1px solid ${D.border}`}}>
          <div style={{maxWidth:1400,margin:"0 auto",padding:"0 20px",display:"flex",justifyContent:"space-between",alignItems:"center",height:52}}>
            <div style={{fontSize:18,fontWeight:700,letterSpacing:2,flexShrink:0}}>
              SHARP<span style={{color:D.oracle}}>INNING</span>
              <span style={{fontSize:8,fontWeight:400,color:D.t2,marginLeft:8,letterSpacing:.8}}>MLB INTEL</span>
            </div>
            <div className="desktop-nav-tabs" style={{gap:2}}>
              {TABS.map(t=>(
                <button key={t.k} onClick={()=>setTab(t.k)}
                  style={{padding:"6px 18px",borderRadius:20,border:`1px solid ${tab===t.k?D.blue:D.borderL}`,background:tab===t.k?`${D.blue}15`:"transparent",cursor:"pointer",fontSize:14,fontWeight:tab===t.k?600:400,color:tab===t.k?D.blue:D.t2,fontFamily:SANS,transition:"all .15s"}}>
                  {t.icon} {t.l}
                </button>
              ))}
            </div>
            <div style={{fontSize:10,color:D.t2,flexShrink:0}}>{today}</div>
          </div>
        </div>

        <div className="left-col" style={{maxWidth:600}}>
          {tab==="board"&&(
            <BoardContent
              oracle={oracleHook.oracle} board={board}
              polls={polls} vote={vote} aiData={aiData}
              analyze={analyze} analyzing={analyzing}
              marketIntelData={marketIntelData}
              analyzeMarketIntel={analyzeMarketIntel}
              marketIntelLoading={marketIntelLoading}
              postGameSummary={postGameSummary}
              fetchPostGameSummary={fetchPostGameSummary}
              summaryLoading={summaryLoading}
              filter={filter} setFilter={setFilter}
              sort={sort} setSort={setSort}
              selDate={selDate} setSelDate={setSelDate}
              processedGames={processedGames}
              liveGames={liveGames} upcoming={upcoming} finals={finals}
              isAvailable={isAvailable}
              onTip={showTip} onGameClick={handleGameClick}
              highlightId={highlightId} gameRefs={gameRefs}
              gameCounts={gameCounts}
              trackRecord={yesterday.record}
            />
          )}
          {tab==="gameintel"&&<GameIntelContent live={live} batters={batters} pitchers={todaysPitchers} hasActiveGames={board.games.some(g=>g.status==="scheduled"||g.status==="live")}/>}
          {tab==="ai"&&<AiTab predictions={aiPreds.predictions} loading={aiPreds.loading} boardGames={board.games} analyze={analyze} analyzing={analyzing} aiData={aiData}/>}
          {tab==="results"&&<ResultsTab yesterday={yesterday} playsTrack={playsTrack}/>}
        </div>

        <div className="right-col" style={{display:"none"}} id="right-col">
          <div style={{marginBottom:20}}>
            <div style={{fontSize:10,fontWeight:600,color:D.t2,letterSpacing:1.5,marginBottom:10}}>LIVE SCORES</div>
            {live.games.length===0?(
              <div style={{background:D.s1,borderRadius:12,padding:"16px",border:`1px solid ${D.border}`,textAlign:"center",color:D.t2,fontSize:14}}>
                No live games · Next game {board.games?.find(g=>g.status==="scheduled")?formatTime(board.games.find(g=>g.status==="scheduled").scheduled_time):"TBD"}
              </div>
            ):(
              live.games.map(g=>(
                <div key={g.game_id} style={{background:D.s1,border:"1px solid rgba(48,209,88,.2)",borderRadius:12,padding:"12px 14px",marginBottom:8}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      <span style={{fontSize:7,color:D.live,animation:"pulse 2s infinite",display:"inline-block"}}>●</span>
                      <span style={{fontSize:16,fontWeight:700}}>{g.away_abbr} @ {g.home_abbr}</span>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:16,fontWeight:700,fontFamily:MONO,color:D.live}}>{g.away_runs??0} — {g.home_runs??0}</div>
                      <div style={{fontSize:10,color:D.t2}}>{g.inning_half} {g.inning}</div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:12}}>
                    <BaseDiamond onFirst={g.on_first} onSecond={g.on_second} onThird={g.on_third} size={48}/>
                    <div style={{fontSize:11,color:D.t2}}>
                      <div>⚾ {g.balls??0}-{g.strikes??0} · {g.outs??0} out{g.outs!==1?"s":""}</div>
                      {g.current_batter&&<div style={{marginTop:2}}>🏏 {g.current_batter}</div>}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <SharpSummary games={board.games} lastUpdated={board.lastUpdated} onGameClick={handleGameClick}/>
        </div>
      </div>

      <div className="mobile-nav glass" style={{position:"fixed",bottom:0,left:0,right:0,zIndex:100,borderTop:`1px solid ${D.border}`}}>
        <div style={{display:"flex",paddingBottom:"env(safe-area-inset-bottom,0px)",maxWidth:600,margin:"0 auto"}}>
          {TABS.map(t=>(
            <button key={t.k} onClick={()=>setTab(t.k)}
              style={{flex:1,padding:"9px 0 8px",background:"none",border:"none",cursor:"pointer",display:"flex",flexDirection:"column",alignItems:"center",gap:2,fontFamily:SANS}}>
              <span style={{fontSize:16,lineHeight:1,opacity:tab===t.k?1:.3}}>{t.icon}</span>
              <span style={{fontSize:8,fontWeight:tab===t.k?700:400,color:tab===t.k?D.blue:D.t3,letterSpacing:.3}}>{t.l}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{position:"fixed",bottom:60,right:14,zIndex:99}}>
        <button onClick={()=>setModal("about")}
          style={{fontSize:9,color:D.t2,background:"rgba(28,28,30,.9)",border:`1px solid ${D.borderL}`,borderRadius:20,padding:"4px 12px",cursor:"pointer",fontFamily:SANS,backdropFilter:"blur(10px)"}}>
          About · 21+
        </button>
      </div>

      {modal&&(
        <div onClick={()=>setModal(null)} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.86)",zIndex:200,display:"flex",alignItems:"flex-end",justifyContent:"center",overflowY:"auto"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:D.s1,border:`1px solid ${D.border}`,borderRadius:"20px 20px 0 0",padding:"20px 20px 44px",width:"100%",maxWidth:560,animation:"fadeUp .3s cubic-bezier(.16,1,.3,1)"}}>
            <div style={{width:32,height:3,background:D.t3,borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{fontSize:20,fontWeight:700,marginBottom:4}}>About SharpInning</div>
            <div style={{fontSize:11,color:D.t3,marginBottom:16,letterSpacing:.5}}>MLB BETTING INTELLIGENCE</div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:D.oracle,letterSpacing:1,marginBottom:6}}>⚡ WHAT IS SHARPINNING?</div>
              <p style={{fontSize:13,color:D.t2,lineHeight:1.8}}>SharpInning surfaces sharp betting angles on every MLB game, every day. We run a multi-factor model across odds, weather, pitching, bullpen fatigue, and market movement — then present the clearest signal we can find.</p>
            </div>
            <div style={{background:D.s2,borderRadius:12,padding:"12px 14px",marginBottom:12,border:`1px solid ${D.borderL}`}}>
              <div style={{fontSize:11,fontWeight:700,color:D.t1,marginBottom:8}}>📊 How the Model Works</div>
              {[
                {label:"Sharp Edge",desc:"The gap between our projected total and the market line — known as Closing Line Value (CLV). The wider the gap, the stronger the signal."},
                {label:"Chaos Score",desc:"0-100 volatility rating. Wind, rain, bullpen fatigue, umpire tendencies, and sharp vs. public money all contribute."},
                {label:"Confidence Tier",desc:"8 factors scored across pitching, weather, market, and environmental data. ELITE → HIGH → MEDIUM → LOW → PASS."},
              ].map(({label,desc})=>(
                <div key={label} style={{marginBottom:8,paddingBottom:8,borderBottom:`1px solid ${D.borderL}`}}>
                  <div style={{fontSize:11,fontWeight:600,color:D.blue,marginBottom:2}}>{label}</div>
                  <div style={{fontSize:11,color:D.t2,lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={{background:"linear-gradient(135deg,#0A0A14,#0D1230)",borderRadius:12,padding:"12px 14px",marginBottom:12,border:"1px solid rgba(90,200,250,.18)"}}>
              <div style={{fontSize:11,fontWeight:700,color:D.oracle,marginBottom:6}}>⚡ The Oracle</div>
              <p style={{fontSize:12,color:D.t2,lineHeight:1.7}}>One pick per day — only when everything lines up. The Oracle fires when a game hits HIGH or ELITE confidence AND carries a meaningful edge. We'd rather go 0-for-1 than spray picks. Quality over quantity.</p>
            </div>
            <div style={{marginBottom:16}}>
              <div style={{fontSize:12,fontWeight:700,color:D.t1,marginBottom:6}}>🎯 Our Mission</div>
              <p style={{fontSize:13,color:D.t2,lineHeight:1.8}}>We surface sharp angles across all major sportsbooks equally — no paid placements, no preferred books. Every book link on this site is chosen for your geo-availability and best available line, not for commission priority.</p>
            </div>
            <div style={{background:"rgba(255,159,10,.06)",border:"1px solid rgba(255,159,10,.2)",borderRadius:10,padding:"10px 12px",marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:D.amber,marginBottom:4}}>⚠ IMPORTANT DISCLAIMER</div>
              <p style={{fontSize:11,color:D.t2,lineHeight:1.65}}>SharpInning is not a sportsbook. All odds and analysis are informational only. Past Oracle performance does not guarantee future results. Sports betting involves risk — only bet what you can afford to lose. Must be 21+ and in a licensed state to wager.</p>
            </div>
            <div style={{background:"rgba(48,209,88,.06)",border:"1px solid rgba(48,209,88,.18)",borderRadius:12,padding:"12px 14px",marginBottom:16}}>
              <div style={{fontSize:10,fontWeight:700,color:D.green,marginBottom:4}}>Gamble Responsibly · Free help 24/7</div>
              <div style={{fontSize:14,color:D.t2}}>📞 <strong style={{color:D.t1}}>1-800-522-4700</strong> — National Problem Gambling Helpline</div>
              <div style={{fontSize:11,color:D.t2,marginTop:2}}>ncpgambling.org</div>
            </div>
            <button onClick={()=>setModal(null)} style={{width:"100%",padding:12,borderRadius:12,border:`1px solid ${D.border}`,background:"transparent",fontSize:13,cursor:"pointer",fontWeight:600,color:D.t2,fontFamily:SANS}}>Close</button>
          </div>
        </div>
      )}

      {tip&&(
        <div onClick={hideTip} style={{position:"fixed",inset:0,background:"rgba(0,0,0,.7)",zIndex:300,display:"flex",alignItems:"flex-end",justifyContent:"center"}}>
          <div onClick={e=>e.stopPropagation()} style={{background:D.s1,border:`1px solid ${D.border}`,borderRadius:"20px 20px 0 0",padding:"20px 20px 44px",width:"100%",maxWidth:560,animation:"fadeUp .25s cubic-bezier(.16,1,.3,1)",maxHeight:"80vh",overflowY:"auto"}}>
            <div style={{width:32,height:3,background:D.t3,borderRadius:2,margin:"0 auto 16px"}}/>
            <div style={{fontSize:15,fontWeight:700,color:D.t1,marginBottom:10}}>{tip.title}</div>
            {tip.body.split("\n\n").map((para,i)=>(
              <p key={i} style={{fontSize:13,color:D.t2,lineHeight:1.75,marginBottom:i<tip.body.split("\n\n").length-1?10:0}}>{para}</p>
            ))}
            <button onClick={hideTip} style={{marginTop:16,width:"100%",padding:12,borderRadius:12,border:`1px solid ${D.borderL}`,background:"transparent",fontSize:13,cursor:"pointer",color:D.t2,fontFamily:SANS}}>Got it</button>
          </div>
        </div>
      )}

      <style>{`
        @media(min-width:1024px){#right-col{display:block!important}.app-shell{max-width:1400px;margin:0 auto}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
      `}</style>
    </div>
  );
}